/** @fileoverview Defines a blank Fabrik app and parses app definition JSON. */

import { z } from 'zod';
import { iconNames, type IconName } from 'lucide-react/dynamic';

import { DEFAULT_LIST_PAGE_SIZE } from '../../constants';
import {
  FABRIK_APP_SIZES,
  FABRIK_BUTTON_VARIANTS,
  FABRIK_COMPONENT_SIZES,
  FABRIK_PANEL_SIZES,
  type FabrikAppDefinition,
} from '../../types/apps';
import { isRecord } from '../../lib/utils';
import {
  GLOBALS_FIELD_TYPES,
  hasRequiredODataFieldType,
  isGlobalField,
} from '../AppRenderer/globals';

export const blankAppDefinition: FabrikAppDefinition = {
  schemaVersion: 3,
  app: { name: 'Untitled app' },
  theme: {},
  screens: [
    {
      id: 'home',
      kind: 'standard',
      label: 'Home',
      components: [
        {
          id: 'welcome',
          kind: 'text',
          style: { variant: 'heading' },
          text: 'Start building',
        },
      ],
    },
  ],
};

export const initialDefinitionJson = JSON.stringify(blankAppDefinition, null, 2);

const hasOnlyKeys = (value: Record<string, unknown>, allowed: string[]) =>
  Object.keys(value).every((key) => allowed.includes(key));
const isAppLogo = (value: unknown) => {
  if (
    !isRecord(value) ||
    (value.color !== undefined && !/^#[0-9A-Fa-f]{6}$/.test(String(value.color)))
  ) {
    return false;
  }
  if (value.type === 'typography') {
    return (
      hasOnlyKeys(value, ['type', 'color', 'style']) &&
      (value.style === undefined ||
        [
          'default',
          'rounded',
          'outline',
          'filled',
          'filled-circle',
          'circle',
          'square',
          'serif',
          'mono',
        ].includes(String(value.style)))
    );
  }
  if (value.type === 'icon') {
    return (
      hasOnlyKeys(value, ['type', 'color', 'icon']) &&
      typeof value.icon === 'string' &&
      iconNames.includes(value.icon as IconName)
    );
  }
  return (
    value.type === 'custom' &&
    hasOnlyKeys(value, ['type', 'color', 'key']) &&
    typeof value.key === 'string' &&
    /^[0-9a-f-]{36}\/fabrik\/apps\/[0-9a-f-]{36}\/logo-[0-9a-f-]{36}\.(png|jpg|svg)$/i.test(
      value.key
    )
  );
};

/* The allowed values as plain string lists, so unknown JSON values can be looked up. */
const BUTTON_VARIANTS: readonly string[] = FABRIK_BUTTON_VARIANTS;
const COMPONENT_SIZES: readonly string[] = FABRIK_COMPONENT_SIZES;
const APP_SIZES: readonly string[] = FABRIK_APP_SIZES;
const PANEL_SIZES: readonly string[] = FABRIK_PANEL_SIZES;

const definitionId = z.string().regex(/^[a-z][a-z0-9_-]{0,63}$/);
const odataIdentifier = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[A-Za-z_][A-Za-z0-9_./]*$/);
/* A field a ref reads from a row: an OData path, or a Workday dotted path with dashed ID types. */
const sourceField = z
  .string()
  .min(1)
  .max(255)
  .regex(/^[A-Za-z_][A-Za-z0-9_./-]*$/);
const scalarValue = z.union([z.string(), z.number(), z.boolean()]);
const recordRefSchema = z
  .object({
    type: z.literal('ref'),
    scope: z.literal('record'),
    dataSourceId: definitionId,
    field: sourceField,
  })
  .strict();
const dataSourceRefSchema = z
  .object({
    type: z.literal('ref'),
    scope: z.literal('dataSource'),
    dataSourceId: definitionId,
    field: sourceField,
  })
  .strict();
const strictRefSchema = z.discriminatedUnion('scope', [
  recordRefSchema,
  z.object({ type: z.literal('ref'), scope: z.literal('variables'), field: definitionId }).strict(),
  z
    .object({
      type: z.literal('ref'),
      scope: z.literal('item'),
      componentId: definitionId,
      field: z.enum([
        'label',
        'owner',
        'owner.name',
        'owner.email',
        'dueDate',
        'checked',
        'text',
        'by',
        'at',
      ]),
    })
    .strict(),
  z
    .object({
      type: z.literal('ref'),
      scope: z.literal('globals'),
      field: z.enum([
        'currentDate',
        'currentDateSap',
        'currentDateTime',
        'currentDateTimeSap',
        'lowDate',
        'highDate',
        'currentUserFirstName',
        'currentUserLastName',
        'currentUserEmail',
      ]),
    })
    .strict(),
  z
    .object({
      type: z.literal('ref'),
      scope: z.literal('inputs'),
      field: definitionId,
      formId: definitionId.optional(),
    })
    .strict(),
  dataSourceRefSchema,
]);
const strictValueRefSchema = strictRefSchema.refine((ref) => ref.scope !== 'dataSource');
const strictValueSchema: z.ZodType = z.lazy(() =>
  z.union([
    z.string(),
    strictValueRefSchema,
    z
      .object({
        type: z.literal('date'),
        from: z.union([z.iso.date(), strictRefSchema]),
        offsetDays: z.number().int().min(-3650).max(3650).optional(),
        format: z.enum(['day', 'dayTime', 'month', 'year']).optional(),
      })
      .strict(),
    z
      .object({
        type: z.literal('template'),
        template: z.string().min(1).max(2000),
        values: z.record(z.string().regex(/^[a-z][a-zA-Z0-9_]{0,31}$/), strictValueSchema),
      })
      .strict(),
    z
      .object({
        type: z.literal('list'),
        item: strictValueSchema,
        separator: z.string().max(10).optional(),
      })
      .strict(),
  ])
);
/* A link blank reads a system setting, the clicked row, or the record on screen. */
const linkSlotRefSchema = z.discriminatedUnion('scope', [
  z
    .object({
      type: z.literal('ref'),
      scope: z.literal('system'),
      systemId: z.uuid(),
      field: z.enum(['webAddress', 'companyId']),
    })
    .strict(),
  dataSourceRefSchema,
  recordRefSchema,
]);
/* A link address with blanks, filled in by the server when the link opens. */
const linkUrlTemplateSchema = z
  .object({
    type: z.literal('template'),
    template: z.string().min(1).max(2000),
    values: z.record(z.string().regex(/^[a-z][a-zA-Z0-9_]{0,31}$/), linkSlotRefSchema),
  })
  .strict();
/** Tells whether a link address is fixed https text or a template with link blanks. */
const isLinkUrl = (url: unknown): boolean =>
  typeof url === 'string'
    ? /^https:\/\//.test(url) && z.url().safeParse(url).success
    : linkUrlTemplateSchema.safeParse(url).success;
const recordKeyRefSchema = z.union([
  z
    .object({
      type: z.literal('ref'),
      scope: z.literal('record'),
      dataSourceId: definitionId,
      field: sourceField,
    })
    .strict(),
  z
    .object({
      type: z.literal('ref'),
      scope: z.literal('globals'),
      field: z.enum([
        'currentDate',
        'currentDateSap',
        'currentDateTime',
        'currentDateTimeSap',
        'lowDate',
        'highDate',
        'currentUserFirstName',
        'currentUserLastName',
        'currentUserEmail',
      ]),
    })
    .strict(),
  z
    .object({
      type: z.literal('ref'),
      scope: z.literal('inputs'),
      field: definitionId,
      formId: definitionId.optional(),
    })
    .strict(),
]);
const inputReference = z.discriminatedUnion('scope', [
  z
    .object({
      type: z.literal('ref'),
      scope: z.literal('record'),
      dataSourceId: definitionId,
      field: sourceField,
    })
    .strict(),
  z.object({ type: z.literal('ref'), scope: z.literal('variables'), field: definitionId }).strict(),
  z
    .object({
      type: z.literal('ref'),
      scope: z.literal('globals'),
      field: z.enum([
        'currentDate',
        'currentDateSap',
        'currentDateTime',
        'currentDateTimeSap',
        'lowDate',
        'highDate',
        'currentUserFirstName',
        'currentUserLastName',
        'currentUserEmail',
      ]),
    })
    .strict(),
  z
    .object({
      type: z.literal('ref'),
      scope: z.literal('inputs'),
      field: definitionId,
      formId: definitionId.optional(),
    })
    .strict(),
]);
const inputRowFilter = z
  .object({
    field: odataIdentifier,
    value: z.union([recordKeyRefSchema]),
    operator: z.enum(['eq', 'ne', 'gt', 'ge', 'lt', 'le', 'contains']).optional(),
    fieldType: z.enum(['Edm.String', 'Edm.DateTime', 'Edm.DateTimeOffset']).optional(),
    omitWhenEmpty: z.boolean().optional(),
    when: z
      .object({
        value: z
          .object({
            type: z.literal('ref'),
            scope: z.literal('inputs'),
            field: definitionId,
            formId: definitionId.optional(),
          })
          .strict(),
        equals: scalarValue,
      })
      .strict()
      .optional(),
  })
  .strict();
/* A day of an effective dated read: a fixed `YYYY-MM-DD` day or a ref, like a row filter value. */
const effectiveDay = z.union([z.iso.date(), recordKeyRefSchema]);
/* Effective dates of an OData read: one day (`asOf`), or a range with at least one end. */
const effectiveDatesSchema = z.union([
  z.object({ asOf: effectiveDay }).strict(),
  z
    .object({ from: effectiveDay.optional(), to: effectiveDay.optional() })
    .strict()
    .refine(({ from, to }) => from !== undefined || to !== undefined),
]);
const inputODataOptions = z
  .object({
    type: z.literal('odata'),
    id: definitionId,
    systemId: z.string().uuid(),
    entity: z
      .string()
      .min(1)
      .max(128)
      .regex(/^[A-Za-z_][A-Za-z0-9_./]*$/),
    fields: z.array(odataIdentifier).max(50).optional(),
    expand: z.array(odataIdentifier).max(10).optional(),
    filter: z
      .string()
      .refine(
        (filter) =>
          !Array.from(filter).some((character) => {
            const code = character.charCodeAt(0);
            return code < 32 || code === 127;
          })
      )
      .optional(),
    orderBy: z
      .array(
        z.object({ field: odataIdentifier, direction: z.enum(['asc', 'desc']).optional() }).strict()
      )
      .min(1)
      .max(3)
      .optional(),
    rowFilters: z.array(inputRowFilter).optional(),
    effectiveDates: effectiveDatesSchema.optional(),
    limit: z.number().int().min(1).max(1000).optional(),
  })
  .strict();
const inputDefinitionSchema = z
  .object({
    id: definitionId,
    kind: z.literal('input'),
    inputType: z.enum([
      'text',
      'textarea',
      'number',
      'email',
      'tel',
      'url',
      'date',
      'time',
      'datetime',
      'select',
      'radio',
      'checkbox',
      'switch',
      'lookup',
    ]),
    label: z.string().trim().min(1).max(80),
    description: z.string().trim().max(500).optional(),
    placeholder: z.string().trim().max(200).optional(),
    required: z.boolean().optional(),
    initialValue: z.union([scalarValue, z.array(scalarValue).max(100), inputReference]).optional(),
    constraints: z
      .union([
        z
          .object({
            minLength: z.number().int().min(0).max(10000).optional(),
            maxLength: z.number().int().min(0).max(10000).optional(),
            pattern: z.string().max(256).optional(),
          })
          .strict(),
        z
          .object({
            min: z.number().finite().optional(),
            max: z.number().finite().optional(),
            step: z.number().positive().finite().optional(),
          })
          .strict(),
        z
          .object({
            min: z.string().optional(),
            max: z.string().optional(),
            step: z.number().positive().finite().optional(),
          })
          .strict(),
      ])
      .optional(),
    valueType: z.enum(['string', 'number', 'boolean']).optional(),
    presentation: z.enum(['default', 'segmented']).optional(),
    fullRow: z.boolean().optional(),
    options: z
      .union([
        z
          .object({
            items: z
              .array(
                z.object({ label: z.string().trim().min(1).max(80), value: scalarValue }).strict()
              )
              .min(1)
              .max(100),
          })
          .strict(),
        z
          .object({
            odata: inputODataOptions,
            label: z
              .object({
                type: z.literal('ref'),
                scope: z.literal('dataSource'),
                dataSourceId: definitionId,
                field: odataIdentifier,
              })
              .strict(),
            value: z
              .object({
                type: z.literal('ref'),
                scope: z.literal('dataSource'),
                dataSourceId: definitionId,
                field: odataIdentifier,
              })
              .strict(),
          })
          .strict(),
        z
          .object({
            systemField: z
              .object({
                systemId: z.string().uuid(),
                /* Entity names may be longer than field names, as on the server. */
                entity: z
                  .string()
                  .min(1)
                  .max(128)
                  .regex(/^[A-Za-z_][A-Za-z0-9_./]*$/),
                field: odataIdentifier,
              })
              .strict(),
          })
          .strict(),
      ])
      .optional(),
  })
  .strict();

/* A titled group of a form's inputs, by id; the server checks that the ids exist. */
const formSectionsSchema = z
  .array(
    z
      .object({
        title: z.string().trim().min(1).max(80),
        note: z.string().trim().min(1).max(200).optional(),
        inputIds: z.array(definitionId).min(1).max(100),
      })
      .strict()
  )
  .min(1)
  .max(20);
const flexItemShape = {
  grow: z.number().int().min(0).max(12).optional(),
  basis: z.enum(['auto', 'sm', 'md', 'lg', 'xl']).optional(),
};
/**
 * Tells what is wrong with a grid's mode, or nothing. A column grid needs columns and keeps
 * every span within them; a flex grid has no columns or spans and sizes items by grow and basis.
 * @param grid the grid or form layout, as read from the definition
 */
const gridModeError = (grid: {
  mode?: unknown;
  columns?: unknown;
  items: { span?: unknown; grow?: unknown; basis?: unknown }[];
}) => {
  if (grid.mode === 'flex') {
    return grid.columns !== undefined || grid.items.some((item) => item.span !== undefined)
      ? 'A flex grid has no columns or spans.'
      : undefined;
  }
  if (typeof grid.columns !== 'number') return 'A column grid needs columns.';
  const { columns } = grid;
  return grid.items.some(
    (item) =>
      item.grow !== undefined ||
      item.basis !== undefined ||
      (typeof item.span === 'number' && item.span > columns)
  )
    ? 'A column grid item cannot set grow or basis, and its span cannot exceed columns.'
    : undefined;
};
const formLayoutItemSchema = z.discriminatedUnion('type', [
  z
    .object({
      type: z.literal('input'),
      inputId: definitionId,
      span: z.number().int().min(1).max(12).optional(),
      align: z.enum(['start', 'center', 'end', 'stretch']).optional(),
      ...flexItemShape,
    })
    .strict(),
  z
    .object({
      type: z.literal('submit'),
      span: z.number().int().min(1).max(12).optional(),
      align: z.enum(['start', 'center', 'end', 'stretch']).optional(),
      ...flexItemShape,
    })
    .strict(),
]);
const formLayoutSchema = z
  .object({
    kind: z.literal('grid'),
    mode: z.enum(['columns', 'flex']).optional(),
    columns: z.number().int().min(1).max(12).optional(),
    gap: z.enum(['sm', 'md', 'lg']),
    items: z.array(formLayoutItemSchema).min(2).max(101),
  })
  .strict();

const choiceInputTypes = new Set(['select', 'radio', 'lookup']);
const stringInputTypes = new Set(['text', 'textarea', 'email', 'tel', 'url']);
const timeValueSchema = z.string().regex(/^(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d(?:\.\d{1,3})?)?$/);
const dateTimeValueSchema = z.string().refine((value) => {
  const [date, time] = value.split('T');
  return (
    date !== undefined &&
    time !== undefined &&
    z.iso.date().safeParse(date).success &&
    timeValueSchema.safeParse(time).success
  );
});

function inputContractError(value: unknown): string {
  const parsed = inputDefinitionSchema.safeParse(value);
  if (!parsed.success) return 'Input does not match the strict v3 contract.';
  const input = parsed.data;
  const choice = choiceInputTypes.has(input.inputType);
  if (choice !== (input.valueType !== undefined) || choice !== (input.options !== undefined))
    return 'Choice inputs need valueType and options; other inputs cannot use them.';
  const scalarType =
    input.inputType === 'number'
      ? 'number'
      : input.inputType === 'checkbox' || input.inputType === 'switch'
      ? 'boolean'
      : input.valueType ?? 'string';
  if (input.constraints) {
    const constraints = input.constraints as unknown as Record<string, unknown>;
    const { minLength, maxLength, pattern, min, max, step } = constraints;
    const keys = Object.keys(constraints);
    const bounds = [min, max].filter((bound) => bound !== undefined);
    const stringBounds = bounds.every((bound) => typeof bound === 'string');
    const numberBounds = bounds.every((bound) => typeof bound === 'number');
    const validStringBounds =
      input.inputType === 'date'
        ? stringBounds && bounds.every((bound) => z.iso.date().safeParse(bound).success)
        : input.inputType === 'time'
        ? stringBounds && bounds.every((bound) => timeValueSchema.safeParse(bound).success)
        : input.inputType === 'datetime'
        ? stringBounds && bounds.every((bound) => dateTimeValueSchema.safeParse(bound).success)
        : false;
    const valid =
      keys.length === 0 ||
      (keys.every((key) => ['minLength', 'maxLength', 'pattern'].includes(key)) &&
        stringInputTypes.has(input.inputType)) ||
      (keys.every((key) => ['min', 'max', 'step'].includes(key)) &&
        input.inputType === 'number' &&
        numberBounds) ||
      (keys.every((key) => ['min', 'max', 'step'].includes(key)) &&
        ['date', 'time', 'datetime'].includes(input.inputType) &&
        validStringBounds) ||
      (keys.length === 1 &&
        step !== undefined &&
        ['number', 'date', 'time', 'datetime'].includes(input.inputType));
    if (
      !valid ||
      (typeof minLength === 'number' && typeof maxLength === 'number' && minLength > maxLength) ||
      (typeof min === 'number' && typeof max === 'number' && min > max) ||
      (typeof min === 'string' && typeof max === 'string' && min > max)
    )
      return 'Constraints are not valid for this input type.';
    if (pattern !== undefined) {
      try {
        new RegExp(String(pattern));
      } catch {
        return 'Input pattern is invalid.';
      }
    }
  }
  const options = input.options;
  if (options && 'items' in options) {
    const values = options.items.map(({ value: item }) => item);
    if (
      values.some((item) => typeof item !== input.valueType) ||
      new Set(values).size !== values.length
    )
      return 'Static option values must be unique and match valueType.';
  }
  if (options && 'odata' in options) {
    const { odata, label, value: optionValue } = options;
    const isAvailable = (field: string) =>
      field.includes('/')
        ? (odata.fields?.includes(field) ?? false) ||
          (odata.expand?.some((path) => field.startsWith(`${path}/`)) ?? false)
        : odata.fields === undefined || odata.fields.includes(field);
    if (
      label.dataSourceId !== odata.id ||
      optionValue.dataSourceId !== odata.id ||
      !isAvailable(label.field) ||
      !isAvailable(optionValue.field)
    )
      return 'Option label and value must reference selected fields from the OData source.';
    if (odata.rowFilters?.some((filter) => !isValidRowFilter(filter)))
      return 'OData option row filters do not match the v3 contract.';
    if (odata.fields && new Set(odata.fields).size !== odata.fields.length)
      return 'Selected OData fields must be unique.';
    if (odata.orderBy) {
      const orderFields = odata.orderBy.map(({ field }) => field);
      if (
        new Set(orderFields).size !== orderFields.length ||
        orderFields.some((field) => !isAvailable(field))
      )
        return 'OData option order fields must be unique and selected.';
    }
  }
  const initial = input.initialValue;
  if (initial !== undefined && (Array.isArray(initial) || !isRecord(initial))) {
    const values = Array.isArray(initial) ? initial : [initial];
    if (Array.isArray(initial) || values.some((item) => typeof item !== scalarType))
      return 'Initial value does not match the input value type.';
    const staticOptions = options && 'items' in options ? options.items : undefined;
    if (
      staticOptions &&
      values.some((item) => !staticOptions.some((option) => option.value === item))
    )
      return 'Initial value must match an available option.';
    if (
      typeof initial === 'string' &&
      ((input.inputType === 'email' && !z.email().safeParse(initial).success) ||
        (input.inputType === 'url' && !z.url().safeParse(initial).success) ||
        (input.inputType === 'date' && !z.iso.date().safeParse(initial).success) ||
        (input.inputType === 'time' && !timeValueSchema.safeParse(initial).success) ||
        (input.inputType === 'datetime' && !dateTimeValueSchema.safeParse(initial).success))
    )
      return 'Initial value is invalid for this input type.';
  }
  return '';
}

const GLOBAL_FIELDS = new Set([
  'currentDate',
  'currentDateSap',
  'currentDateTime',
  'currentDateTimeSap',
  'lowDate',
  'highDate',
  'currentUserFirstName',
  'currentUserLastName',
  'currentUserEmail',
]);
const ITEM_FIELDS = new Set([
  'label',
  'owner',
  'owner.name',
  'owner.email',
  'dueDate',
  'checked',
  'text',
  'by',
  'at',
]);
const isDefinitionId = (value: unknown): value is string =>
  typeof value === 'string' && /^[a-z][a-z0-9_-]{0,63}$/.test(value);
function isReferenceShape(value: unknown): value is Record<string, unknown> {
  if (!isRecord(value) || value.type !== 'ref' || typeof value.scope !== 'string') return false;
  switch (value.scope) {
    case 'record':
    case 'dataSource':
      return (
        hasOnlyKeys(value, ['type', 'scope', 'dataSourceId', 'field']) &&
        isDefinitionId(value.dataSourceId) &&
        sourceField.safeParse(value.field).success
      );
    case 'variables':
    case 'inputs':
      return (
        hasOnlyKeys(
          value,
          value.scope === 'inputs'
            ? ['type', 'scope', 'field', 'formId']
            : ['type', 'scope', 'field']
        ) &&
        isDefinitionId(value.field) &&
        (value.scope !== 'inputs' || value.formId === undefined || isDefinitionId(value.formId))
      );
    case 'globals':
      return (
        hasOnlyKeys(value, ['type', 'scope', 'field']) &&
        typeof value.field === 'string' &&
        GLOBAL_FIELDS.has(value.field)
      );
    case 'item':
      return (
        hasOnlyKeys(value, ['type', 'scope', 'componentId', 'field']) &&
        isDefinitionId(value.componentId) &&
        typeof value.field === 'string' &&
        ITEM_FIELDS.has(value.field)
      );
    default:
      return false;
  }
}

function isValueShape(value: unknown, allowList = true): boolean {
  if (typeof value === 'string') return true;
  if (!isRecord(value) || typeof value.type !== 'string') return false;
  if (value.type === 'ref') return strictValueRefSchema.safeParse(value).success;
  if (value.type === 'date')
    return (
      hasOnlyKeys(value, ['type', 'from', 'offsetDays']) &&
      (typeof value.from === 'string' || strictValueRefSchema.safeParse(value.from).success) &&
      (value.offsetDays === undefined ||
        (typeof value.offsetDays === 'number' &&
          Number.isInteger(value.offsetDays) &&
          Math.abs(value.offsetDays) <= 3650))
    );
  if (value.type === 'template')
    return (
      hasOnlyKeys(value, ['type', 'template', 'values']) &&
      typeof value.template === 'string' &&
      value.template.length >= 1 &&
      value.template.length <= 2000 &&
      isRecord(value.values) &&
      Object.entries(value.values).every(
        ([key, entry]) => /^[a-z][a-zA-Z0-9_]{0,31}$/.test(key) && isValueShape(entry, allowList)
      )
    );
  if (value.type === 'list')
    return (
      allowList &&
      hasOnlyKeys(value, ['type', 'item', 'separator']) &&
      isValueShape(value.item, false) &&
      (value.separator === undefined ||
        (typeof value.separator === 'string' && value.separator.length <= 10))
    );
  return false;
}

const isDisplayValue = (value: unknown) =>
  typeof value === 'string' ||
  (strictValueRefSchema.safeParse(value).success && isRecord(value) && value.scope !== 'item');
const isHeaderValue = (value: unknown) =>
  typeof value === 'string'
    ? value.trim().length > 0 && value.trim().length <= 120
    : isDisplayValue(value);
const isRecipientValue = (value: unknown) =>
  typeof value === 'string'
    ? z.email().safeParse(value).success
    : strictValueRefSchema.safeParse(value).success;
const isDateGivingValue = (value: unknown) =>
  typeof value === 'string'
    ? z.iso.date().safeParse(value).success
    : isRecord(value) && value.type === 'date'
    ? isValueShape(value)
    : strictValueRefSchema.safeParse(value).success;
const isNameValue = (value: unknown): boolean => {
  if (typeof value === 'string') return value.trim().length > 0 && value.trim().length <= 120;
  if (strictValueRefSchema.safeParse(value).success)
    return isRecord(value) && value.scope !== 'item';
  return (
    isRecord(value) &&
    value.type === 'template' &&
    hasOnlyKeys(value, ['type', 'template', 'values']) &&
    typeof value.template === 'string' &&
    value.template.length >= 1 &&
    value.template.length <= 2000 &&
    isRecord(value.values) &&
    Object.entries(value.values).every(
      ([key, entry]) =>
        /^[a-z][a-zA-Z0-9_]{0,31}$/.test(key) &&
        (typeof entry === 'string' ||
          (strictValueRefSchema.safeParse(entry).success &&
            isRecord(entry) &&
            entry.scope !== 'item'))
    )
  );
};

function isTargetShape(value: unknown, allowAction = false): boolean {
  if (!isRecord(value) || typeof value.type !== 'string') return false;
  if (value.type === 'screen')
    return hasOnlyKeys(value, ['type', 'screenId']) && isDefinitionId(value.screenId);
  if (value.type === 'link') return hasOnlyKeys(value, ['type', 'url']) && isLinkUrl(value.url);
  return (
    allowAction &&
    value.type === 'action' &&
    hasOnlyKeys(value, ['type', 'actionId']) &&
    isDefinitionId(value.actionId)
  );
}

/* The keys each action step kind may have. */
const ACTION_STEP_KEYS: Record<string, string[]> = {
  store: ['kind', 'target', 'rows'],
  write: ['kind', 'target', 'intent', 'values', 'onFailure'],
  send: ['kind', 'to', 'items', 'groupBy', 'subject', 'text'],
  export: ['kind', 'from', 'format', 'document', 'to'],
};

/** Checks the question an action asks before it runs. */
const isActionConfirmShape = (value: unknown): boolean =>
  isRecord(value) &&
  hasOnlyKeys(value, ['title', 'message', 'button']) &&
  [
    [value.title, 80],
    [value.message, 500],
    [value.button, 40],
  ].every(
    ([text, maxLength]) =>
      typeof text === 'string' && text.trim().length > 0 && text.trim().length <= Number(maxLength)
  );

/**
 * Checks a write step to a connected system: its target, intent and the fields it sends. The
 * server checks the fields against the system metadata.
 * @param step the write step to check
 */
const isSystemWriteStepShape = (step: Record<string, unknown>): boolean => {
  const { target, intent, values, onFailure } = step;
  return (
    isRecord(target) &&
    hasOnlyKeys(target, ['systemId', 'entity']) &&
    typeof target.systemId === 'string' &&
    isODataIdentifier(target.entity, 128) &&
    ['create', 'update', 'createOrUpdate'].includes(String(intent)) &&
    Array.isArray(values) &&
    values.length >= 1 &&
    values.length <= 100 &&
    values.every(
      (entry) =>
        isRecord(entry) &&
        hasOnlyKeys(entry, ['field', 'value']) &&
        isODataIdentifier(entry.field) &&
        isValueShape(entry.value, false)
    ) &&
    (onFailure === undefined || onFailure === 'stop' || onFailure === 'continue')
  );
};

const CHART_TYPES: readonly string[] = ['bar', 'donut', 'line'];
const CHART_INTERVALS: readonly string[] = ['day', 'week', 'month', 'quarter', 'year'];
/** Most charts one screen may hold, as the server allows. */
const MAX_CHARTS_PER_SCREEN = 4;

/**
 * Tells whether a chart column reference is a column name.
 * @param value the column name to check
 */
const isChartColumnName = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0 && value.length <= 64;

/**
 * Checks one chart's own shape: its type, list, columns, interval and measure. Whether the
 * columns exist on the list is checked with the screen, in `validateCharts`.
 * @param component the chart component to check
 */
const isValidChartShape = (component: Record<string, unknown>): boolean => {
  const { type, from, groupBy, interval, splitBy, measure } = component;
  if (!CHART_TYPES.includes(String(type)) || !isDefinitionId(from) || !isChartColumnName(groupBy)) {
    return false;
  }
  if (interval !== undefined && (type !== 'line' || !CHART_INTERVALS.includes(String(interval)))) {
    return false;
  }
  if (
    splitBy !== undefined &&
    (type === 'donut' || !isChartColumnName(splitBy) || splitBy === groupBy)
  ) {
    return false;
  }
  if (measure === undefined) {
    return true;
  }
  if (!isRecord(measure)) {
    return false;
  }
  return measure.fn === 'count'
    ? hasOnlyKeys(measure, ['fn'])
    : (measure.fn === 'sum' || measure.fn === 'average') &&
        hasOnlyKeys(measure, ['fn', 'column']) &&
        isChartColumnName(measure.column);
};

/** Gives back a copy of an object without one key. */
const omitKey = (value: Record<string, unknown>, key: string): Record<string, unknown> =>
  Object.fromEntries(Object.entries(value).filter(([entryKey]) => entryKey !== key));

/** Rejects malformed strict-v3 structures and unsupported discriminators before rendering. */
function validateStrictShapes(definition: FabrikAppDefinition): string {
  const validateNestedRefs = (value: unknown): boolean => {
    if (Array.isArray(value)) return value.every(validateNestedRefs);
    if (!isRecord(value)) return true;
    /* List columns, joins and computed inputs read their own row; the server checks each place. */
    if (value.type === 'ref') return strictRefSchema.safeParse(value).success;
    /* A link address takes link blanks only, which the general value shapes do not cover. */
    if (value.type === 'link' && 'url' in value) return isLinkUrl(value.url);
    /* A PDF fields item `link` and a PDF table `link.url` take link blanks like a link target. */
    if (value.kind === 'table' && isRecord(value.link)) {
      const { url } = value.link;
      return isLinkUrl(url) && Object.values(omitKey(value, 'link')).every(validateNestedRefs);
    }
    if (value.kind === 'fields' && Array.isArray(value.items)) {
      const linksValid = value.items.every(
        (item) => !isRecord(item) || !('link' in item) || isLinkUrl(item.link)
      );
      const items = value.items.map((item) =>
        isRecord(item) && 'link' in item ? omitKey(item, 'link') : item
      );
      return linksValid && Object.values({ ...value, items }).every(validateNestedRefs);
    }
    /* A list column's `type` is its display type, so a value is told apart by its own key. */
    const isValue =
      (value.type === 'date' && 'from' in value) ||
      (value.type === 'template' && 'template' in value) ||
      (value.type === 'list' && 'item' in value);
    if (isValue && !strictValueSchema.safeParse(value).success) return false;
    return Object.values(value).every(validateNestedRefs);
  };
  if (!validateNestedRefs(definition))
    return 'Reference or value does not match the strict v3 contract.';
  const root = definition as unknown as Record<string, unknown>;
  if (
    !hasOnlyKeys(root, [
      'schemaVersion',
      'app',
      'theme',
      'navigation',
      'variables',
      'userGroups',
      'screens',
      'actions',
      'documents',
    ])
  )
    return 'Definition contains unsupported fields.';
  if (root.documents !== undefined && !isValidPdfDocumentsShape(root.documents))
    return 'Documents must be a list of at most 20 PDF documents, each with an id, a label and blocks.';
  if (
    !isRecord(root.app) ||
    !hasOnlyKeys(root.app, ['name', 'description']) ||
    typeof root.app.name !== 'string' ||
    root.app.name.trim().length < 1 ||
    root.app.name.trim().length > 80 ||
    (root.app.description !== undefined &&
      (typeof root.app.description !== 'string' || root.app.description.trim().length > 300))
  )
    return 'App contains unsupported or invalid fields.';
  if (
    root.theme !== undefined &&
    (!isRecord(root.theme) ||
      !hasOnlyKeys(root.theme, ['primaryColor', 'size', 'logo']) ||
      (root.theme.logo !== undefined && !isAppLogo(root.theme.logo)) ||
      (root.theme.primaryColor !== undefined &&
        (typeof root.theme.primaryColor !== 'string' ||
          !/^#[0-9A-Fa-f]{6}$/.test(root.theme.primaryColor))) ||
      (root.theme.size !== undefined && !APP_SIZES.includes(String(root.theme.size))))
  )
    return 'Theme contains unsupported or invalid fields.';
  if (
    root.variables !== undefined &&
    (!Array.isArray(root.variables) ||
      root.variables.some(
        (variable) =>
          !isRecord(variable) || !hasOnlyKeys(variable, ['id', 'label', 'type', 'value'])
      ))
  )
    return 'Variables contain unsupported fields.';

  if (
    root.navigation !== undefined &&
    (!isRecord(root.navigation) ||
      !hasOnlyKeys(root.navigation, ['screens']) ||
      !Array.isArray(root.navigation.screens) ||
      !root.navigation.screens.every(isDefinitionId))
  )
    return 'Navigation does not match the strict v3 contract.';

  const allowed: Record<string, string[]> = {
    text: ['id', 'kind', 'style', 'text', 'size'],
    icon: ['id', 'kind', 'name', 'size', 'label'],
    metric: ['id', 'kind', 'label', 'supportingText', 'dataSource', 'size'],
    grid: ['id', 'kind', 'mode', 'columns', 'gap', 'items'],
    card: ['id', 'kind', 'title', 'note', 'content'],
    button: ['id', 'kind', 'style', 'label', 'target', 'showWhen', 'size'],
    list: [
      'id',
      'kind',
      'title',
      'columns',
      'limit',
      'dataSource',
      'primaryDataSourceId',
      'joins',
      'target',
      'headerActions',
      'resultCount',
      'sortChoices',
      'filters',
    ],
    checklist: ['id', 'kind', 'title', 'recordKey', 'itemActions'],
    timeline: ['id', 'kind', 'title', 'recordKey'],
    fields: ['id', 'kind', 'items'],
    chart: [
      'id',
      'kind',
      'title',
      'type',
      'from',
      'groupBy',
      'interval',
      'splitBy',
      'measure',
      'size',
    ],
    input: [
      'id',
      'kind',
      'inputType',
      'label',
      'description',
      'placeholder',
      'required',
      'initialValue',
      'constraints',
      'valueType',
      'options',
      'presentation',
      'fullRow',
    ],
    form: ['id', 'kind', 'title', 'inputs', 'layout', 'sections', 'submit'],
  };
  const screens = definition.screens as unknown[];
  for (const screen of screens) {
    if (
      !isRecord(screen) ||
      !hasOnlyKeys(screen, [
        'id',
        'kind',
        'label',
        'displayType',
        'size',
        'header',
        'components',
      ]) ||
      !isDefinitionId(screen.id) ||
      !['standard', 'detail'].includes(String(screen.kind)) ||
      typeof screen.label !== 'string' ||
      screen.label.trim().length < 1 ||
      screen.label.trim().length > 80 ||
      !Array.isArray(screen.components) ||
      screen.components.length < 1 ||
      screen.components.length > 30 ||
      (screen.displayType !== undefined &&
        !['sidebar', 'page'].includes(String(screen.displayType))) ||
      (screen.size !== undefined &&
        (screen.displayType !== 'sidebar' || !PANEL_SIZES.includes(String(screen.size))))
    )
      return 'Screen does not match the strict v3 contract.';
    if (
      screen.header !== undefined &&
      (!isRecord(screen.header) ||
        !hasOnlyKeys(screen.header, ['title', 'subtitle']) ||
        !Array.isArray(screen.header.title) ||
        screen.header.title.length < 1 ||
        screen.header.title.length > 3 ||
        !screen.header.title.every(isHeaderValue) ||
        (screen.header.subtitle !== undefined &&
          (!Array.isArray(screen.header.subtitle) ||
            screen.header.subtitle.length < 1 ||
            screen.header.subtitle.length > 3 ||
            !screen.header.subtitle.every(isHeaderValue))))
    )
      return 'Screen header does not match the strict v3 contract.';
    const components = screen.components;
    const inspect = (component: unknown, parentLayoutPath: ('grid' | 'card')[] = []): string => {
      if (!isRecord(component) || typeof component.kind !== 'string' || !allowed[component.kind])
        return 'Unsupported component discriminator.';
      const layoutKind: 'grid' | 'card' | undefined =
        component.kind === 'grid' ? 'grid' : component.kind === 'card' ? 'card' : undefined;
      const layoutPath = layoutKind ? [...parentLayoutPath, layoutKind] : parentLayoutPath;
      const validLayoutPath =
        !layoutKind ||
        layoutPath.length === 1 ||
        (layoutPath[0] === 'grid' &&
          layoutPath[1] === 'card' &&
          (layoutPath.length === 2 || (layoutPath.length === 3 && layoutPath[2] === 'grid'))) ||
        (layoutPath.length === 2 && layoutPath[0] === 'card' && layoutPath[1] === 'grid');
      if (!validLayoutPath)
        return 'Grid/Card nesting only supports Grid → Card → Grid or Card → Grid.';
      if (!hasOnlyKeys(component, allowed[component.kind]) || !isDefinitionId(component.id))
        return `Component "${String(component.id)}" contains unsupported fields.`;
      if (
        component.kind !== 'icon' &&
        component.size !== undefined &&
        !COMPONENT_SIZES.includes(String(component.size))
      )
        return `Component "${String(component.id)}" size must be sm, md, lg or xl.`;
      if (
        component.kind !== 'card' &&
        component.title !== undefined &&
        (typeof component.title !== 'string' || component.title.trim().length > 80)
      )
        return 'Component title is invalid.';
      if (component.kind === 'grid') {
        if (
          (component.mode !== undefined && !['columns', 'flex'].includes(String(component.mode))) ||
          (component.columns !== undefined &&
            (!Number.isInteger(component.columns) ||
              Number(component.columns) < 1 ||
              Number(component.columns) > 12)) ||
          !['sm', 'md', 'lg'].includes(String(component.gap)) ||
          !Array.isArray(component.items) ||
          component.items.length < 1
        )
          return 'Grid does not match the strict v3 contract.';
        for (const item of component.items) {
          if (
            !isRecord(item) ||
            !hasOnlyKeys(item, ['component', 'span', 'align', 'grow', 'basis']) ||
            (item.span !== undefined && (!Number.isInteger(item.span) || Number(item.span) < 1)) ||
            (item.grow !== undefined &&
              (!Number.isInteger(item.grow) || Number(item.grow) < 0 || Number(item.grow) > 12)) ||
            (item.basis !== undefined &&
              !['auto', 'sm', 'md', 'lg', 'xl'].includes(String(item.basis))) ||
            (item.align !== undefined &&
              !['start', 'center', 'end', 'stretch'].includes(String(item.align)))
          )
            return 'Grid item has an invalid span, size or alignment.';
          const error = inspect(item.component, layoutPath);
          if (error) return error;
        }
        const modeError = gridModeError({
          mode: component.mode,
          columns: component.columns,
          items: component.items as { span?: unknown; grow?: unknown; basis?: unknown }[],
        });
        if (modeError) return modeError;
      }
      if (component.kind === 'card') {
        if (
          (component.title !== undefined && typeof component.title !== 'string') ||
          (component.note !== undefined && typeof component.note !== 'string')
        )
          return 'Card title and note must be strings.';
        const error = inspect(component.content, layoutPath);
        if (error) return error;
      }
      if (
        component.kind === 'icon' &&
        (typeof component.name !== 'string' ||
          component.name.trim().length < 1 ||
          component.name.trim().length > 64 ||
          !/^[A-Za-z0-9]+(?:-[A-Za-z0-9]+)*$/.test(component.name.trim()) ||
          (component.size !== undefined &&
            !COMPONENT_SIZES.includes(String(component.size)) &&
            (!Number.isInteger(component.size) ||
              Number(component.size) < 12 ||
              Number(component.size) > 96)) ||
          (component.label !== undefined &&
            (typeof component.label !== 'string' ||
              !component.label.trim() ||
              component.label.trim().length > 120)))
      )
        return 'Icon needs a valid Lucide name, an optional size step or size from 12 to 96, and an optional accessible label.';
      if (
        component.kind === 'metric' &&
        (typeof component.label !== 'string' ||
          !component.label.trim() ||
          component.label.trim().length > 80 ||
          (component.supportingText !== undefined &&
            typeof component.supportingText !== 'string') ||
          !isRecord(component.dataSource) ||
          !['odata', 'workday', 'items'].includes(String(component.dataSource.type)))
      )
        return 'Metric does not match the strict v3 contract.';
      if (component.kind === 'input') {
        const error = inputContractError(component);
        if (error) return `Input "${String(component.id)}": ${error}`;
      }
      if (component.kind === 'form') {
        const formLayout =
          component.layout === undefined ? undefined : formLayoutSchema.safeParse(component.layout);
        if (
          !Array.isArray(component.inputs) ||
          component.inputs.length < 1 ||
          component.inputs.length > 100 ||
          !isRecord(component.submit) ||
          !hasOnlyKeys(component.submit, ['label', 'style', 'target']) ||
          typeof component.submit.label !== 'string' ||
          component.submit.label.trim().length < 1 ||
          component.submit.label.trim().length > 40 ||
          !isRecord(component.submit.style) ||
          !hasOnlyKeys(component.submit.style, ['variant']) ||
          !BUTTON_VARIANTS.includes(String(component.submit.style.variant)) ||
          (component.submit.target !== undefined &&
            !isTargetShape(component.submit.target, true)) ||
          (component.sections !== undefined &&
            !formSectionsSchema.safeParse(component.sections).success) ||
          (component.layout !== undefined && !formLayout?.success)
        )
          return 'Form does not match the strict v3 contract.';
        for (const input of component.inputs) {
          const error = inputContractError(input);
          if (error) return `Form "${String(component.id)}" contains an invalid input: ${error}`;
        }
        const configuredInputIds = component.inputs.map((input) =>
          isRecord(input) ? String(input.id) : ''
        );
        const sectionInputIds: unknown[] = Array.isArray(component.sections)
          ? component.sections.flatMap((section) =>
              isRecord(section) && Array.isArray(section.inputIds) ? section.inputIds : []
            )
          : [];
        if (
          new Set(sectionInputIds).size !== sectionInputIds.length ||
          sectionInputIds.some((id) => typeof id !== 'string' || !configuredInputIds.includes(id))
        )
          return 'Form sections must reference unique inputs from the form.';
        if (formLayout?.success) {
          const layout = formLayout.data;
          const inputIds = layout.items.flatMap((item) =>
            item.type === 'input' ? [item.inputId] : []
          );
          if (
            layout.items.filter((item) => item.type === 'submit').length !== 1 ||
            gridModeError(layout) !== undefined ||
            inputIds.length !== configuredInputIds.length ||
            new Set(inputIds).size !== inputIds.length ||
            configuredInputIds.some((id) => !inputIds.includes(id))
          )
            return 'Form grid must place each input exactly once and include one submit item.';
        }
      }
      if (
        component.kind === 'text' &&
        (!isRecord(component.style) ||
          !hasOnlyKeys(component.style, ['variant']) ||
          !['heading', 'subheading', 'body', 'note', 'eyebrow', 'display'].includes(
            String(component.style.variant)
          ) ||
          !isDisplayValue(component.text))
      )
        return 'Text component does not match the strict v3 contract.';
      if (
        component.kind === 'text' &&
        typeof component.text === 'string' &&
        component.text.length > 2000
      )
        return 'Text must be at most 2000 characters.';
      if (
        component.kind === 'button' &&
        ((typeof component.label === 'string' &&
          (component.label.trim().length < 1 || component.label.length > 40)) ||
          !isRecord(component.style) ||
          !hasOnlyKeys(component.style, ['variant']) ||
          !BUTTON_VARIANTS.includes(String(component.style.variant)) ||
          !isDisplayValue(component.label) ||
          !isTargetShape(component.target, true) ||
          (component.showWhen !== undefined &&
            (!isRecord(component.showWhen) ||
              !hasOnlyKeys(component.showWhen, ['componentId', 'rows']) ||
              !isDefinitionId(component.showWhen.componentId) ||
              !['none', 'any'].includes(String(component.showWhen.rows)))))
      )
        return 'Button does not match the strict v3 contract.';
      if (
        component.kind === 'list' &&
        ((component.target !== undefined && !isTargetShape(component.target)) ||
          !Array.isArray(component.columns) ||
          !Array.isArray(component.dataSource) ||
          component.dataSource.some(
            (source) =>
              !isRecord(source) || !['items', 'odata', 'workday'].includes(String(source.type))
          ))
      )
        return 'List does not match the strict v3 contract.';
      if (component.kind === 'list') {
        for (const column of component.columns as unknown[]) {
          if (!isRecord(column)) return 'List columns must use a supported discriminator.';
          if (column.kind === 'computed') {
            if (
              !hasOnlyKeys(column, ['kind', 'name', 'compute', 'inputs', 'label']) ||
              !isDefinitionId(column.name) ||
              !['daysUntil', 'daysSince', 'yearsSince', 'join'].includes(String(column.compute)) ||
              !isRecord(column.inputs) ||
              typeof column.label !== 'string' ||
              !column.label.trim() ||
              column.label.trim().length > 40
            )
              return 'Computed column does not match the strict v3 contract.';
            const expected = column.compute === 'join' ? ['fields', 'separator'] : ['date'];
            if (Object.keys(column.inputs).some((key) => !expected.includes(key)))
              return 'Computed column contains unsupported inputs.';
            if (column.compute === 'join') {
              if (
                !Array.isArray(column.inputs.fields) ||
                column.inputs.fields.length < 2 ||
                column.inputs.fields.length > 5 ||
                !column.inputs.fields.every(
                  (ref) => isReferenceShape(ref) && ref.scope === 'dataSource'
                ) ||
                (column.inputs.separator !== undefined &&
                  (typeof column.inputs.separator !== 'string' ||
                    column.inputs.separator.length > 10))
              )
                return 'Join column inputs are invalid.';
            } else if (
              !isReferenceShape(column.inputs.date) ||
              !['dataSource', 'globals'].includes(String(column.inputs.date.scope))
            )
              return 'Date calculation needs a row field or date global.';
          } else if (column.kind === 'person') {
            if (
              !hasOnlyKeys(column, ['kind', 'name', 'label', 'primary', 'secondary']) ||
              !isDefinitionId(column.name) ||
              typeof column.label !== 'string' ||
              !column.label.trim() ||
              column.label.trim().length > 40 ||
              !isReferenceShape(column.primary) ||
              column.primary.scope !== 'dataSource' ||
              (column.secondary !== undefined &&
                (!isReferenceShape(column.secondary) || column.secondary.scope !== 'dataSource'))
            )
              return 'Person column does not match the strict v3 contract.';
          } else if (column.kind === 'status') {
            if (
              !hasOnlyKeys(column, ['kind', 'name', 'label', 'value', 'options']) ||
              !isDefinitionId(column.name) ||
              typeof column.label !== 'string' ||
              !column.label.trim() ||
              column.label.trim().length > 40 ||
              !isReferenceShape(column.value) ||
              column.value.scope !== 'dataSource' ||
              !Array.isArray(column.options) ||
              column.options.length < 1 ||
              column.options.length > 20 ||
              column.options.some(
                (option) =>
                  !isRecord(option) ||
                  !hasOnlyKeys(option, ['value', 'label', 'tone']) ||
                  !['string', 'number', 'boolean'].includes(typeof option.value) ||
                  typeof option.label !== 'string' ||
                  !option.label.trim() ||
                  option.label.trim().length > 40 ||
                  !['neutral', 'positive', 'warning', 'negative'].includes(String(option.tone))
              )
            )
              return 'Status column does not match the strict v3 contract.';
            const values = column.options.map(
              (option) => (option as Record<string, unknown>).value
            );
            if (new Set(values).size !== values.length)
              return 'Status column values must be unique.';
          } else if (
            !hasOnlyKeys(column, ['kind', 'name', 'value', 'label', 'type']) ||
            (column.kind !== undefined && column.kind !== 'field') ||
            !isDefinitionId(column.name) ||
            !isReferenceShape(column.value) ||
            column.value.scope !== 'dataSource' ||
            typeof column.label !== 'string' ||
            !column.label.trim() ||
            column.label.trim().length > 40 ||
            !['text', 'number', 'date', 'url'].includes(String(column.type))
          )
            return 'Field column does not match the strict v3 contract.';
        }
        if (
          component.sortChoices !== undefined &&
          (!Array.isArray(component.sortChoices) ||
            component.sortChoices.some(
              (choice) =>
                !isRecord(choice) ||
                !hasOnlyKeys(choice, ['id', 'label', 'orderBy']) ||
                !isDefinitionId(choice.id) ||
                typeof choice.label !== 'string' ||
                !choice.label.trim() ||
                choice.label.trim().length > 80 ||
                !isValidOrderBy(choice.orderBy)
            ))
        )
          return 'List sort choices are invalid.';
        if (component.filters !== undefined) {
          if (!Array.isArray(component.filters)) return 'List filters are invalid.';
          for (const filter of component.filters) {
            if (
              !isRecord(filter) ||
              !hasOnlyKeys(filter, ['id', 'label', 'field', 'options', 'fieldType']) ||
              !isDefinitionId(filter.id) ||
              typeof filter.label !== 'string' ||
              !filter.label.trim() ||
              filter.label.trim().length > 80 ||
              !isODataIdentifier(filter.field) ||
              !Array.isArray(filter.options) ||
              filter.options.length < 1 ||
              filter.options.length > 100 ||
              filter.options.some(
                (option) =>
                  !isRecord(option) ||
                  !hasOnlyKeys(option, ['label', 'value']) ||
                  typeof option.label !== 'string' ||
                  !option.label.trim() ||
                  option.label.trim().length > 80 ||
                  !['string', 'number', 'boolean'].includes(typeof option.value)
              ) ||
              (filter.fieldType !== undefined && !odataFieldTypes.has(String(filter.fieldType)))
            )
              return 'List filters are invalid.';
          }
        }
        if (component.resultCount !== undefined && typeof component.resultCount !== 'boolean')
          return 'List resultCount must be boolean.';
        if (
          component.joins !== undefined &&
          (!Array.isArray(component.joins) ||
            component.joins.some(
              (join) =>
                !isRecord(join) ||
                !hasOnlyKeys(join, ['dataSourceId', 'field', 'on', 'fieldType']) ||
                !isDefinitionId(join.dataSourceId) ||
                !isODataIdentifier(join.field) ||
                !isReferenceShape(join.on) ||
                !['dataSource', 'globals'].includes(String(join.on.scope)) ||
                (join.fieldType !== undefined && !odataFieldTypes.has(String(join.fieldType)))
            ))
        )
          return 'List joins do not match the strict v3 contract.';
      }
      if (
        component.kind === 'checklist' &&
        component.itemActions !== undefined &&
        (!Array.isArray(component.itemActions) ||
          component.itemActions.length < 1 ||
          component.itemActions.length > 3 ||
          component.itemActions.some(
            (item) =>
              !isRecord(item) ||
              !hasOnlyKeys(item, ['label', 'target']) ||
              typeof item.label !== 'string' ||
              item.label.trim().length < 1 ||
              item.label.trim().length > 40 ||
              !isTargetShape(item.target, true) ||
              !isRecord(item.target) ||
              item.target.type !== 'action'
          ))
      )
        return 'Checklist item actions do not match the strict v3 contract.';
      if (
        (component.kind === 'checklist' || component.kind === 'timeline') &&
        (!Array.isArray(component.recordKey) ||
          component.recordKey.length < 1 ||
          component.recordKey.length > 5 ||
          !component.recordKey.every((ref) => recordKeyRefSchema.safeParse(ref).success))
      )
        return 'State component record key does not match the strict v3 contract.';
      if (
        component.kind === 'fields' &&
        (!Array.isArray(component.items) ||
          component.items.length < 1 ||
          component.items.length > 30 ||
          component.items.some(
            (item) =>
              !isRecord(item) ||
              !hasOnlyKeys(item, ['label', 'value']) ||
              typeof item.label !== 'string' ||
              item.label.trim().length < 1 ||
              item.label.trim().length > 80 ||
              !isDisplayValue(item.value)
          ))
      )
        return 'Fields component does not match the strict v3 contract.';
      if (component.kind === 'chart' && !isValidChartShape(component))
        return `Chart "${String(
          component.id
        )}" needs a type of bar, donut or line, a list id in from, a groupBy column, and a valid interval, splitBy and measure.`;
      return '';
    };
    for (const component of components) {
      const error = inspect(component);
      if (error) return error;
    }
  }

  if (root.actions !== undefined) {
    if (!Array.isArray(root.actions) || root.actions.length > 50)
      return 'Actions must be a list of at most 50 entries.';
    for (const action of root.actions) {
      if (
        !isRecord(action) ||
        !hasOnlyKeys(action, ['id', 'label', 'confirm', 'allowedGroups', 'atomic', 'steps']) ||
        !isDefinitionId(action.id) ||
        (action.atomic !== undefined && typeof action.atomic !== 'boolean') ||
        typeof action.label !== 'string' ||
        action.label.trim().length < 1 ||
        action.label.trim().length > 80 ||
        (action.confirm !== undefined && !isActionConfirmShape(action.confirm)) ||
        !Array.isArray(action.steps) ||
        action.steps.length < 1 ||
        action.steps.length > 10
      )
        return 'Action does not match the strict v3 contract.';
      for (const step of action.steps) {
        if (!isRecord(step) || !Object.hasOwn(ACTION_STEP_KEYS, String(step.kind)))
          return 'Unsupported action step discriminator.';
        if (!hasOnlyKeys(step, ACTION_STEP_KEYS[String(step.kind)]))
          return 'Action step contains unsupported fields.';
        if (step.kind === 'write' && !isSystemWriteStepShape(step))
          return 'Write step does not match the strict v3 contract.';
        if (
          step.kind === 'store' &&
          (!isRecord(step.target) ||
            !hasOnlyKeys(step.target, ['componentId']) ||
            !isDefinitionId(step.target.componentId) ||
            !Array.isArray(step.rows) ||
            step.rows.length < 1 ||
            step.rows.length > 100)
        )
          return 'Store step does not match the strict v3 contract.';
        if (
          step.kind === 'send' &&
          (!isValidSendTo(step.to) ||
            (step.subject !== undefined &&
              !(
                (typeof step.subject === 'string' &&
                  step.subject.trim().length > 0 &&
                  step.subject.trim().length <= 200) ||
                (isRecord(step.subject) &&
                  step.subject.type === 'template' &&
                  isValueShape(step.subject))
              )) ||
            !isRecord(step.text) ||
            step.text.type !== 'template' ||
            !isValueShape(step.text) ||
            (step.items !== undefined &&
              (!isRecord(step.items) ||
                !hasOnlyKeys(step.items, ['componentId', 'filter']) ||
                !isDefinitionId(step.items.componentId) ||
                !['open', 'overdue'].includes(String(step.items.filter)))) ||
            (step.groupBy !== undefined && step.groupBy !== 'to'))
        )
          return 'Send step does not match the strict v3 contract.';
      }
    }
  }
  return '';
}

const isODataIdentifier = (value: unknown, maxLength = 64) =>
  typeof value === 'string' &&
  value.length > 0 &&
  value.length <= maxLength &&
  /^[A-Za-z_][A-Za-z0-9_./]*$/.test(value);

/** Checks one OData `orderBy` entry: a valid field and an optional asc/desc direction, nothing else. */
const isValidOrderByEntry = (orderEntry: Record<string, unknown>) => {
  const { field, direction } = orderEntry;
  return (
    hasOnlyKeys(orderEntry, ['field', 'direction']) &&
    isODataIdentifier(field) &&
    (direction === undefined || direction === 'asc' || direction === 'desc')
  );
};

/** Checks an OData `orderBy` list of 1 to 3 valid entries. */
const isValidOrderBy = (value: unknown) =>
  Array.isArray(value) &&
  value.length >= 1 &&
  value.length <= 3 &&
  value.every((orderEntry) => isRecord(orderEntry) && isValidOrderByEntry(orderEntry));

const odataFieldTypes = new Set(['Edm.String', 'Edm.DateTime', 'Edm.DateTimeOffset']);
const rowFilterOperators = new Set(['eq', 'ne', 'gt', 'ge', 'lt', 'le', 'contains']);

/** Checks a row filter's value: a typed record or globals ref. */
const isValidRowFilterRef = (value: unknown) => {
  if (!isRecord(value)) return false;
  const { type, scope, dataSourceId, field } = value;
  if (type !== 'ref') return false;
  if (scope === 'globals') {
    return hasOnlyKeys(value, ['type', 'scope', 'field']) && isGlobalField(field);
  }
  if (scope === 'inputs') {
    return (
      hasOnlyKeys(value, ['type', 'scope', 'field', 'formId']) &&
      typeof field === 'string' &&
      /^[a-z][a-z0-9_-]{0,63}$/.test(field) &&
      (value.formId === undefined ||
        (typeof value.formId === 'string' && /^[a-z][a-z0-9_-]{0,63}$/.test(value.formId)))
    );
  }
  return (
    scope === 'record' &&
    hasOnlyKeys(value, ['type', 'scope', 'dataSourceId', 'field']) &&
    typeof dataSourceId === 'string' &&
    /^[a-z][a-z0-9_-]{0,63}$/.test(dataSourceId) &&
    sourceField.safeParse(field).success
  );
};

/** Checks one OData row filter; a date or time globals value also needs the target field type. */
const isValidRowFilter = (value: unknown) => {
  if (
    !isRecord(value) ||
    !hasOnlyKeys(value, ['field', 'value', 'operator', 'fieldType', 'omitWhenEmpty', 'when']) ||
    !isODataIdentifier(value.field) ||
    !isValidRowFilterRef(value.value) ||
    (value.operator !== undefined &&
      (typeof value.operator !== 'string' || !rowFilterOperators.has(value.operator))) ||
    (value.fieldType !== undefined &&
      (typeof value.fieldType !== 'string' || !odataFieldTypes.has(value.fieldType))) ||
    (value.operator === 'contains' &&
      value.fieldType !== undefined &&
      value.fieldType !== 'Edm.String') ||
    (value.omitWhenEmpty !== undefined && typeof value.omitWhenEmpty !== 'boolean') ||
    (value.when !== undefined &&
      (!isRecord(value.when) ||
        !hasOnlyKeys(value.when, ['value', 'equals']) ||
        !isValidRowFilterRef(value.when.value) ||
        !isRecord(value.when.value) ||
        value.when.value.scope !== 'inputs' ||
        !['string', 'number', 'boolean'].includes(typeof value.when.equals)))
  ) {
    return false;
  }
  return hasRequiredODataFieldType(value.value as Record<string, unknown>, value.fieldType);
};

/**
 * Yields every component of every screen in reading order, including nested Grid and Card children.
 * Skips entries that are not objects, since the definition comes from hand-edited JSON.
 * @param definition the parsed app definition
 */
function* eachComponent(definition: FabrikAppDefinition): Generator<Record<string, unknown>> {
  const visit = function* (component: unknown): Generator<Record<string, unknown>> {
    if (!isRecord(component)) return;
    yield component;
    if (component.kind === 'grid' && Array.isArray(component.items)) {
      for (const item of component.items) {
        if (isRecord(item)) yield* visit(item.component);
      }
    } else if (component.kind === 'card') {
      yield* visit(component.content);
    }
  };
  for (const screen of definition.screens) {
    if (!isRecord(screen) || !Array.isArray(screen.components)) continue;
    for (const component of screen.components) yield* visit(component);
  }
}

/**
 * Rejects layout shapes the renderer cannot draw, since the JSON may be hand-edited.
 * Does not look inside values, so the typed ref and date shapes pass through untouched.
 */
function validateLayoutShapes(definition: FabrikAppDefinition): string {
  for (const screen of definition.screens) {
    if (!isRecord(screen) || !('header' in screen) || screen.header === undefined) continue;
    const { header } = screen;
    if (
      !isRecord(header) ||
      !Array.isArray(header.title) ||
      header.title.length === 0 ||
      (header.subtitle !== undefined && !Array.isArray(header.subtitle))
    ) {
      return `Screen "${String(screen.id)}" has a header without a title list.`;
    }
  }
  for (const component of eachComponent(definition)) {
    if (component.kind === 'grid' && !Array.isArray(component.items)) {
      return `Grid "${String(component.id)}" needs an items list.`;
    }
    if (component.kind === 'card' && !('content' in component)) {
      return `Card "${String(component.id)}" needs content.`;
    }
  }
  return '';
}

const workdayName = /^[A-Za-z_][A-Za-z0-9_-]*$/;
const workdayEntity = /^[A-Za-z_][A-Za-z0-9_]*\.[A-Za-z_][A-Za-z0-9_]*$/;
const workdayFieldPath = /^[A-Za-z_][A-Za-z0-9_-]*(\.[A-Za-z_][A-Za-z0-9_-]*)*$/;

/**
 * Tells whether a Workday value is fixed text or a ref.
 * @param value the value of a record ID or criteria
 */
const isWorkdayValue = (value: unknown) =>
  (typeof value === 'string' && value.length > 0 && value.length <= 256) ||
  (isRecord(value) && value.type === 'ref');

/**
 * Tells whether a Workday ID pair has an ID type and a value.
 * @param pair the ID pair
 */
const isWorkdayIdPair = (pair: unknown) =>
  isRecord(pair) &&
  hasOnlyKeys(pair, ['type', 'value']) &&
  typeof pair.type === 'string' &&
  workdayName.test(pair.type) &&
  isWorkdayValue(pair.value);

/**
 * Checks a Workday source: entity `<Service>.<Get operation>`, 1 to 50 dotted field paths, and
 * well formed references, criteria, as-of day and response group switches.
 * @param source the source
 */
const readWorkdaySourceError = (source: Record<string, unknown>): string => {
  const { systemId, entity, values, references, criteria, asOf, responseGroup } = source;
  const isValid =
    hasOnlyKeys(source, [
      'type',
      'id',
      'systemId',
      'entity',
      'values',
      'references',
      'criteria',
      'asOf',
      'responseGroup',
    ]) &&
    typeof systemId === 'string' &&
    z.uuid().safeParse(systemId).success &&
    typeof entity === 'string' &&
    entity.length <= 160 &&
    workdayEntity.test(entity) &&
    Array.isArray(values) &&
    values.length >= 1 &&
    values.length <= 50 &&
    values.every(
      (value) => typeof value === 'string' && value.length <= 255 && workdayFieldPath.test(value)
    ) &&
    (references === undefined ||
      (Array.isArray(references) &&
        references.length >= 1 &&
        references.length <= 100 &&
        references.every(isWorkdayIdPair))) &&
    (criteria === undefined ||
      (isRecord(criteria) &&
        Object.entries(criteria).every(
          ([name, value]) =>
            workdayName.test(name) &&
            (typeof value === 'boolean' || isWorkdayValue(value) || isWorkdayIdPair(value))
        ))) &&
    (asOf === undefined || isWorkdayValue(asOf)) &&
    (responseGroup === undefined ||
      (isRecord(responseGroup) &&
        Object.entries(responseGroup).every(
          ([name, value]) => workdayName.test(name) && typeof value === 'boolean'
        )));
  return isValid ? '' : 'Workday datasource is invalid.';
};

/** Rejects list and metric datasource shapes outside the strict v3 schema. */
function validateListConfiguration(definition: FabrikAppDefinition): string {
  for (const component of eachComponent(definition)) {
    if (component.kind === 'metric') {
      const source = component.dataSource;
      if (!isRecord(source) || !isDefinitionId(source.id))
        return 'Metric datasource needs a valid id.';
      if (source.type === 'items') {
        if (
          !hasOnlyKeys(source, ['type', 'id', 'items']) ||
          !Array.isArray(source.items) ||
          source.items.length < 1 ||
          source.items.length > 100 ||
          source.items.some(
            (item) =>
              !isRecord(item) ||
              !hasOnlyKeys(item, ['id', 'values']) ||
              !isDefinitionId(item.id) ||
              !isRecord(item.values) ||
              Object.values(item.values).some(
                (value) => !['string', 'number', 'boolean'].includes(typeof value)
              )
          )
        )
          return 'Metric static datasource is invalid.';
      } else if (source.type === 'workday') {
        const workdayError = readWorkdaySourceError(source);
        if (workdayError) return workdayError;
      } else if (
        source.type !== 'odata' ||
        !hasOnlyKeys(source, [
          'type',
          'id',
          'systemId',
          'entity',
          'values',
          'expand',
          'filter',
          'orderBy',
          'rowFilters',
        ]) ||
        typeof source.systemId !== 'string' ||
        !z.uuid().safeParse(source.systemId).success ||
        !isODataIdentifier(source.entity, 128) ||
        (source.values !== undefined &&
          (!Array.isArray(source.values) ||
            source.values.length > 50 ||
            !source.values.every((field) => isODataIdentifier(field)))) ||
        (source.expand !== undefined &&
          (!Array.isArray(source.expand) ||
            source.expand.length > 10 ||
            !source.expand.every((path) => isODataIdentifier(path)))) ||
        (source.filter !== undefined &&
          (typeof source.filter !== 'string' ||
            Array.from(source.filter).some(
              (character) => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127
            ))) ||
        (source.orderBy !== undefined && !isValidOrderBy(source.orderBy)) ||
        (source.rowFilters !== undefined &&
          (!Array.isArray(source.rowFilters) || !source.rowFilters.every(isValidRowFilter)))
      )
        return 'Metric OData datasource is invalid.';
      continue;
    }
    if (component.kind !== 'list') continue;
    if (
      !hasOnlyKeys(component, [
        'id',
        'kind',
        'title',
        'columns',
        'limit',
        'dataSource',
        'primaryDataSourceId',
        'joins',
        'target',
        'headerActions',
        'resultCount',
        'sortChoices',
        'filters',
      ])
    ) {
      return 'List contains unsupported fields.';
    }
    if (
      component.limit !== undefined &&
      (typeof component.limit !== 'number' ||
        !Number.isInteger(component.limit) ||
        component.limit < 1 ||
        component.limit > 100)
    ) {
      return 'List limit must be a whole number between 1 and 100.';
    }
    if (!Array.isArray(component.dataSource) || component.dataSource.length === 0) {
      return 'A list must define one or more datasources in an array.';
    }
    if (typeof component.primaryDataSourceId !== 'string') {
      return 'A list must identify its primary datasource.';
    }
    if (component.dataSource.some((source) => !isRecord(source) || !isDefinitionId(source.id))) {
      return 'Every list datasource must have an id.';
    }
    const sources = component.dataSource as Record<string, unknown>[];
    if (!sources.some((source) => source.id === component.primaryDataSourceId)) {
      return 'The primary datasource must exist in this list.';
    }
    for (const source of sources) {
      if (source.type === 'items') {
        if (
          !hasOnlyKeys(source, ['type', 'id', 'items']) ||
          !Array.isArray(source.items) ||
          source.items.length < 1 ||
          source.items.length > 100 ||
          source.items.some(
            (item) =>
              !isRecord(item) ||
              !hasOnlyKeys(item, ['id', 'values']) ||
              !isDefinitionId(item.id) ||
              !isRecord(item.values) ||
              Object.values(item.values).some(
                (value) =>
                  typeof value !== 'string' &&
                  typeof value !== 'number' &&
                  typeof value !== 'boolean'
              )
          )
        ) {
          return 'Static datasources must contain between 1 and 100 supported items.';
        }
        continue;
      }
      if (source.type === 'workday') {
        const workdayError = readWorkdaySourceError(source);
        if (workdayError) return workdayError;
        continue;
      }
      if (
        source.type !== 'odata' ||
        !hasOnlyKeys(source, [
          'type',
          'id',
          'systemId',
          'entity',
          'values',
          'expand',
          'filter',
          'orderBy',
          'rowFilters',
          'effectiveDates',
        ])
      ) {
        return 'Unsupported list datasource type or fields.';
      }
      if (
        typeof source.systemId !== 'string' ||
        !z.uuid().safeParse(source.systemId).success ||
        !isODataIdentifier(source.entity, 128)
      ) {
        return 'OData datasources require a system and valid entity.';
      }
      for (const [field, maxLength] of [
        ['values', 50],
        ['expand', 10],
      ] as const) {
        const entries = source[field];
        if (
          entries !== undefined &&
          (!Array.isArray(entries) ||
            entries.length > maxLength ||
            !entries.every((entry) => isODataIdentifier(entry)))
        ) {
          return `OData ${field} must contain at most ${maxLength} valid identifiers.`;
        }
      }
      const filter = source.filter;
      if (
        filter !== undefined &&
        (typeof filter !== 'string' ||
          Array.from(filter).some((character) => {
            const code = character.charCodeAt(0);
            return code < 32 || code === 127;
          }))
      ) {
        return 'OData filters must be strings without control characters.';
      }
      if (source.orderBy !== undefined && !isValidOrderBy(source.orderBy)) {
        return 'OData orderBy must contain 1 to 3 entries, each with a valid field and an optional direction of asc or desc.';
      }
      if (
        source.rowFilters !== undefined &&
        (!Array.isArray(source.rowFilters) || !source.rowFilters.every(isValidRowFilter))
      ) {
        return 'OData rowFilters must contain valid field, value, operator, and fieldType values.';
      }
      if (source.effectiveDates !== undefined && !isValidEffectiveDates(source.effectiveDates)) {
        return 'OData effectiveDates must be asOf alone, or from and to with at least one, each a YYYY-MM-DD day or a reference.';
      }
    }
  }
  return '';
}

/**
 * Lists the days of an effective dates object, in the order asOf, from, to.
 * @param effectiveDates the effective dates, read as data
 */
const effectiveDaysOf = (effectiveDates: Record<string, unknown>): unknown[] =>
  (['asOf', 'from', 'to'] as const).flatMap((key) =>
    effectiveDates[key] === undefined ? [] : [effectiveDates[key]]
  );

/**
 * Checks the shape of an OData source's effective dates: `asOf` alone, or `from` and `to` with
 * at least one of them. Each day is a `YYYY-MM-DD` day or a ref as a row filter takes.
 * @param value the effective dates, read as data
 */
const isValidEffectiveDates = (value: unknown) => {
  if (!isRecord(value)) {
    return false;
  }
  const allowedKeys = value.asOf === undefined ? ['from', 'to'] : ['asOf'];
  const days = effectiveDaysOf(value);
  return (
    hasOnlyKeys(value, allowedKeys) &&
    days.length > 0 &&
    days.every((day) =>
      typeof day === 'string' ? z.iso.date().safeParse(day).success : isValidRowFilterRef(day)
    )
  );
};

/**
 * Tells whether a key has already been seen, recording it for next time.
 * Used to flag the first duplicate key in a single pass over a list.
 * @param seenKeys the keys recorded so far
 * @param key the key to check and record
 */
const isDuplicateKey = (seenKeys: Set<string>, key: string): boolean => {
  if (seenKeys.has(key)) {
    return true;
  }
  seenKeys.add(key);
  return false;
};

/** Rejects a `variables` list that is not unique by id, or has a bad email value. Plain messages. */
function validateVariables(definition: FabrikAppDefinition): string {
  const { variables } = definition as { variables?: unknown };
  if (variables === undefined) {
    return '';
  }
  if (!Array.isArray(variables) || variables.length > 50) {
    return 'Variables must be a list of at most 50 entries.';
  }
  const seenIds = new Set<string>();
  for (const variable of variables) {
    if (
      !isRecord(variable) ||
      !isDefinitionId(variable.id) ||
      typeof variable.label !== 'string' ||
      variable.label.trim().length < 1 ||
      variable.label.trim().length > 80 ||
      (variable.type !== 'email' && variable.type !== 'text' && variable.type !== 'date') ||
      typeof variable.value !== 'string'
    ) {
      return 'Each variable needs an id, a label, a type, and a value.';
    }
    if (isDuplicateKey(seenIds, variable.id)) {
      return `Variable id "${variable.id}" is used more than once.`;
    }
    if (variable.type === 'email' && !z.email().safeParse(variable.value).success) {
      return `Variable "${variable.id}" needs a valid email value.`;
    }
    if (variable.type === 'date' && !z.iso.date().safeParse(variable.value).success) {
      return `Variable "${variable.id}" needs a date value in YYYY-MM-DD format.`;
    }
    if (variable.type === 'text' && (variable.value.length < 1 || variable.value.length > 500)) {
      return `Variable "${variable.id}" needs a text value between 1 and 500 characters.`;
    }
  }
  return '';
}

/* Most user groups one app may hold, and most emails one group may list. */
const USER_GROUP_LIMIT = 10;
const USER_GROUP_EMAIL_LIMIT = 200;

/**
 * Checks the user groups and the groups each action allows. Returns an error message, or empty
 * text.
 * @param definition definition to check
 * @param emailsHidden true for a hosted app, whose groups come without their emails
 */
function validateUserGroups(definition: FabrikAppDefinition, emailsHidden: boolean): string {
  const { userGroups, actions } = definition as { userGroups?: unknown; actions?: unknown };
  const groupIds = new Set<string>();
  if (userGroups !== undefined) {
    if (!Array.isArray(userGroups) || userGroups.length > USER_GROUP_LIMIT) {
      return `User groups must be a list of at most ${USER_GROUP_LIMIT} entries.`;
    }
    for (const group of userGroups) {
      if (
        !isRecord(group) ||
        !hasOnlyKeys(group, ['id', 'label', 'emails']) ||
        !isDefinitionId(group.id) ||
        typeof group.label !== 'string' ||
        group.label.trim().length < 1 ||
        group.label.trim().length > 80
      ) {
        return 'Each user group needs an id and a label.';
      }
      const { emails } = group;
      if (
        (!emailsHidden || emails !== undefined) &&
        (!Array.isArray(emails) ||
          emails.length < 1 ||
          emails.length > USER_GROUP_EMAIL_LIMIT ||
          emails.some((email) => !z.email().safeParse(email).success))
      ) {
        return `User group "${group.id}" needs 1 to ${USER_GROUP_EMAIL_LIMIT} valid emails.`;
      }
      if (
        Array.isArray(emails) &&
        new Set(emails.map((email) => String(email).toLowerCase())).size !== emails.length
      ) {
        return `User group "${group.id}" lists an email more than once.`;
      }
      if (isDuplicateKey(groupIds, group.id)) {
        return `User group id "${group.id}" is used more than once.`;
      }
    }
  }
  for (const action of Array.isArray(actions) ? actions : []) {
    if (!isRecord(action) || action.allowedGroups === undefined) {
      continue;
    }
    const { allowedGroups } = action;
    if (
      !Array.isArray(allowedGroups) ||
      allowedGroups.length < 1 ||
      allowedGroups.length > USER_GROUP_LIMIT ||
      new Set(allowedGroups).size !== allowedGroups.length
    ) {
      return `Action "${String(
        action.id
      )}" needs 1 to ${USER_GROUP_LIMIT} different allowed groups.`;
    }
    const missing = allowedGroups.find((groupId) => !groupIds.has(String(groupId)));
    if (missing !== undefined) {
      return `Action "${String(action.id)}" allows user group "${String(
        missing
      )}", which does not exist.`;
    }
  }
  return '';
}

/**
 * Checks a list of 1 to 3 action buttons with a 1 to 40 character label and an action target.
 * Returns an error message, or empty text.
 * @param entries the checklist's `itemActions` or the list's `headerActions`
 * @param names how the messages name the block and one entry, and whether entries may carry a style
 */
const actionEntriesError = (
  entries: unknown,
  names: { block: string; entry: string; allowsStyle?: boolean }
): string => {
  const { block, entry, allowsStyle = false } = names;
  if (!Array.isArray(entries) || entries.length < 1 || entries.length > 3) {
    return `A ${block} must have between 1 and 3 ${entry}s.`;
  }
  for (const actionEntry of entries) {
    if (
      !isRecord(actionEntry) ||
      typeof actionEntry.label !== 'string' ||
      actionEntry.label.trim().length < 1 ||
      actionEntry.label.trim().length > 40 ||
      !isRecord(actionEntry.target) ||
      actionEntry.target.type !== 'action' ||
      typeof actionEntry.target.actionId !== 'string'
    ) {
      return `Each ${entry} needs a label and an action target.`;
    }
    const { style } = actionEntry;
    if (style !== undefined && !allowsStyle) {
      return `A ${entry} cannot have a style.`;
    }
    if (
      style !== undefined &&
      (!isRecord(style) || !BUTTON_VARIANTS.includes(String(style.variant)))
    ) {
      return `Each ${entry} style must be primary, secondary, ghost or danger.`;
    }
  }
  return '';
};

/**
 * Rejects a checklist's `itemActions` or a list's `headerActions` when it is not the shape the
 * renderer and server accept.
 * @param definition the parsed app definition
 */
function validateItemActions(definition: FabrikAppDefinition): string {
  for (const component of eachComponent(definition)) {
    const error =
      component.kind === 'checklist' && component.itemActions !== undefined
        ? actionEntriesError(component.itemActions, { block: 'checklist', entry: 'item action' })
        : component.kind === 'list' && component.headerActions !== undefined
        ? actionEntriesError(component.headerActions, {
            block: 'list',
            entry: 'header action',
            allowsStyle: true,
          })
        : '';
    if (error) {
      return error;
    }
  }
  return '';
}

/**
 * Checks a send step's `to`: the typed `{ type: "email", address }` shape, or an older bare
 * value, which the server turns into the typed shape.
 * @param to the step's `to` value
 */
const isValidSendTo = (to: unknown): boolean =>
  isRecord(to) && to.type === 'email'
    ? hasOnlyKeys(to, ['type', 'address']) && isRecipientValue(to.address)
    : isRecipientValue(to);

/**
 * Checks one `send` step's shape: `to` and `text` are required, `items` (when given) names a
 * component and a filter, and `groupBy` (when given) is the only value the server accepts.
 * No template is rendered here; the server resolves `text` and `subject` at run time.
 * @param step the send step to check
 */
function isValidSendStepShape(step: Record<string, unknown>): boolean {
  if (!isValidSendTo(step.to) || step.text === undefined) {
    return false;
  }
  if (step.items !== undefined) {
    const { items } = step;
    if (
      !isRecord(items) ||
      typeof items.componentId !== 'string' ||
      !items.componentId ||
      (items.filter !== 'open' && items.filter !== 'overdue')
    ) {
      return false;
    }
  }
  return step.groupBy === undefined || step.groupBy === 'to';
}

/**
 * Checks an owner value. A flat owner is a string or a `{ type: "ref" }` object. Any other object
 * must hold only `name` and `email`, with `email` present. Returns an error message, or empty text.
 * @param owner the owner value of one write row
 */
const ownerShapeError = (owner: unknown): string => {
  if (!isRecord(owner)) {
    return 'An owner needs an email address, an email field or an email variable.';
  }
  if (!hasOnlyKeys(owner, ['name', 'email'])) {
    return 'An owner may only have name and email.';
  }
  return owner.email === undefined
    ? 'An owner needs an email address, an email field or an email variable.'
    : '';
};

/**
 * Checks the owner of every row in a `write` step.
 * @param step the write step to check
 */
function validateWriteRows(step: Record<string, unknown>): string {
  if (!Array.isArray(step.rows)) {
    return '';
  }
  for (const row of step.rows) {
    if (isRecord(row) && row.owner !== undefined) {
      const error = ownerShapeError(row.owner);
      if (error) {
        return error;
      }
    }
  }
  return '';
}

/**
 * Checks the shared PDF documents' shape: at most 20, each with an id, a label and a list of
 * blocks. The server checks the blocks themselves when the app is saved.
 * @param documents the app's `documents` value
 */
const isValidPdfDocumentsShape = (documents: unknown): boolean =>
  Array.isArray(documents) &&
  documents.length <= 20 &&
  documents.every(
    (document) =>
      isRecord(document) &&
      isDefinitionId(document.id) &&
      typeof document.label === 'string' &&
      Array.isArray(document.blocks)
  );

/**
 * Checks one `export` step's shape: a list to read for `xlsx`, or the id of a shared document
 * for `pdf`, and a `to` that downloads the file, emails it or saves it to SharePoint, with a file
 * name. The server checks the rest when the app is saved.
 * @param step the export step to check
 */
const isValidExportStepShape = (step: Record<string, unknown>): boolean => {
  const { from, format, document, to } = step;
  if (format === 'xlsx') {
    if (!isRecord(from) || typeof from.componentId !== 'string' || !from.componentId) {
      return false;
    }
  } else if (format !== 'pdf' || !isDefinitionId(document)) {
    return false;
  }
  if (!isRecord(to) || to.fileName === undefined) {
    return false;
  }
  if (to.type === 'sharepoint') {
    return typeof to.systemId === 'string' && to.systemId !== '' && typeof to.folder === 'string';
  }
  return to.type === 'download' || (to.type === 'email' && to.address !== undefined);
};

/** Rejects an action whose `send`, `write`, or `export` steps do not match the shape the server accepts. */
function validateActions(definition: FabrikAppDefinition): string {
  const { actions } = definition as { actions?: unknown };
  if (actions === undefined) {
    return '';
  }
  if (!Array.isArray(actions)) {
    return 'Actions must be a list.';
  }
  for (const action of actions) {
    if (!isRecord(action) || !Array.isArray(action.steps)) {
      continue;
    }
    for (const step of action.steps) {
      if (!isRecord(step)) {
        return 'Each action step must be an object.';
      }
      if (step.kind === 'send' && !isValidSendStepShape(step)) {
        return 'A send step needs a recipient and message text, with valid items and groupBy values.';
      }
      if (step.kind === 'export' && !isValidExportStepShape(step)) {
        return 'An export step needs a list for xlsx or a document id for pdf, and a download, email or SharePoint target with a file name. A SharePoint target needs a systemId and a folder.';
      }
      if (step.kind === 'store') {
        const rowsError = validateWriteRows(step);
        if (rowsError) {
          return rowsError;
        }
      }
    }
  }
  return '';
}

function validateDefinitionLinks(definition: FabrikAppDefinition): string {
  const screens = definition.screens;
  if (screens[0]?.kind !== 'standard') return 'The first screen must be standard.';
  if (
    screens.some(
      (screen) =>
        (screen.kind === 'standard' && screen.displayType !== undefined) ||
        (screen.kind !== 'detail' && screen.header !== undefined)
    )
  )
    return 'Only detail screens can have a display type or header.';
  const screenIds = new Set(screens.map((screen) => screen.id));
  const actionIds = new Set((definition.actions ?? []).map((action) => action.id));
  const components = [...eachComponent(definition)];
  const componentIds = new Set<string>();
  const componentScreens = new Map<string, string>();
  const stateComponents = new Set<string>();
  const stateKinds = new Map<string, string>();
  const actionItemSources = new Map<string, Set<string>>();
  const inputKey = (formId: unknown, field: unknown) =>
    `${typeof formId === 'string' ? formId : ''}:${String(field)}`;
  const inputByRefKey = new Map<string, { screenId: string; input: Record<string, unknown> }>();
  const inputDependencies = new Map<string, Set<string>>();
  const variableIds = new Set((definition.variables ?? []).map(({ id }) => id));
  const variablesById = new Map(
    (definition.variables ?? []).map((variable) => [variable.id, variable])
  );
  const dataSourceIds = new Set<string>();
  const navigation = new Map(screens.map(({ id }) => [id, new Set<string>()]));
  const actionScreens = new Map<string, string>();
  const register = (component: Record<string, unknown>, screenId: string) => {
    if (component.kind === 'grid' && Array.isArray(component.items)) {
      component.items.forEach((item) => {
        if (isRecord(item) && isRecord(item.component)) register(item.component, screenId);
      });
      return;
    }
    if (component.kind === 'card' && isRecord(component.content)) {
      register(component.content, screenId);
      return;
    }
    componentScreens.set(String(component.id), screenId);
    if (component.kind === 'input')
      inputByRefKey.set(inputKey(undefined, component.id), { screenId, input: component });
    if (component.kind === 'form' && Array.isArray(component.inputs)) {
      component.inputs.forEach((input) => {
        if (isRecord(input))
          inputByRefKey.set(inputKey(component.id, input.id), { screenId, input });
      });
    }
    const targets =
      component.kind === 'form'
        ? [isRecord(component.submit) ? component.submit.target : undefined]
        : component.kind === 'button' || component.kind === 'list'
        ? [component.target]
        : [];
    targets.forEach((target) => {
      if (isRecord(target) && target.type === 'screen' && navigation.has(String(target.screenId)))
        navigation.get(screenId)?.add(String(target.screenId));
    });
    /* Buttons, checklist item actions, list header actions and form submits run actions. */
    const runEntries =
      component.kind === 'button'
        ? [component]
        : component.kind === 'form'
        ? [component.submit]
        : component.kind === 'checklist' && Array.isArray(component.itemActions)
        ? component.itemActions
        : component.kind === 'list' && Array.isArray(component.headerActions)
        ? component.headerActions
        : [];
    const actionTargets = runEntries.flatMap((entry) =>
      isRecord(entry) && isRecord(entry.target) && entry.target.type === 'action'
        ? [entry.target.actionId]
        : []
    );
    actionTargets.forEach((id) => {
      if (typeof id === 'string')
        actionScreens.set(
          id,
          actionScreens.has(id) && actionScreens.get(id) !== screenId ? '' : screenId
        );
    });
    if (component.kind === 'checklist' && Array.isArray(component.itemActions))
      component.itemActions.forEach((item) => {
        if (isRecord(item) && isRecord(item.target) && typeof item.target.actionId === 'string') {
          const sources = actionItemSources.get(item.target.actionId) ?? new Set<string>();
          sources.add(String(component.id));
          actionItemSources.set(item.target.actionId, sources);
        }
      });
  };
  screens.forEach((screen) =>
    screen.components.forEach((component) => {
      if (isRecord(component)) register(component, screen.id);
    })
  );
  for (const component of components) {
    const id = String(component.id);
    if (componentIds.has(id)) return `Component id "${id}" is used more than once.`;
    componentIds.add(id);
    if (component.kind === 'checklist' || component.kind === 'timeline') {
      stateComponents.add(id);
      stateKinds.set(id, component.kind);
    }
    if (component.kind === 'form' && Array.isArray(component.inputs)) {
      const formInputs = new Set<string>();
      for (const input of component.inputs) {
        if (!isRecord(input) || typeof input.id !== 'string') continue;
        if (formInputs.has(input.id))
          return `Input id "${input.id}" is used more than once in form "${id}".`;
        formInputs.add(input.id);
      }
    }
    if (component.kind === 'list' && Array.isArray(component.dataSource)) {
      component.dataSource.forEach((source) => {
        if (isRecord(source) && typeof source.id === 'string') dataSourceIds.add(source.id);
      });
    }
  }
  if (screenIds.size !== screens.length) return 'Screen ids must be unique.';
  if (actionIds.size !== (definition.actions ?? []).length) return 'Action ids must be unique.';
  if (definition.navigation) {
    const tabs = definition.navigation.screens;
    if (new Set(tabs).size !== tabs.length || tabs.some((id) => !screenIds.has(id)))
      return 'Navigation must reference unique existing screen ids.';
  }
  const canReach = (from: string, to: string) => {
    const pending = [from];
    const visited = new Set<string>();
    while (pending.length) {
      const current = pending.pop() as string;
      if (current === to) return true;
      if (visited.has(current)) continue;
      visited.add(current);
      pending.push(...(navigation.get(current) ?? []));
    }
    return false;
  };
  const inputScalarType = (input: Record<string, unknown>) =>
    input.inputType === 'number'
      ? 'number'
      : input.inputType === 'checkbox' || input.inputType === 'switch'
      ? 'boolean'
      : input.valueType ?? 'string';
  const fieldAvailable = (source: Record<string, unknown>, field: string) => {
    if (source.type === 'items')
      return (
        Array.isArray(source.items) &&
        source.items.every(
          (item) => isRecord(item) && isRecord(item.values) && Object.hasOwn(item.values, field)
        )
      );
    const values = Array.isArray(source.values) ? source.values : undefined;
    // A Workday row holds each value path plus the ID paths under a link value.
    if (source.type === 'workday')
      return Boolean(
        values?.some(
          (path) => typeof path === 'string' && (field === path || field.startsWith(`${path}.`))
        )
      );
    const expand = Array.isArray(source.expand) ? source.expand : [];
    return field.includes('/')
      ? Boolean(
          values?.includes(field) ||
            expand.some((path) => typeof path === 'string' && field.startsWith(`${path}/`))
        )
      : values === undefined || values.includes(field);
  };
  const needsInputFieldType = (ref: Record<string, unknown>, fieldType: unknown) => {
    if (fieldType || ref.scope !== 'inputs') return false;
    const placed = inputByRefKey.get(inputKey(ref.formId, ref.field));
    return Boolean(placed && ['date', 'time', 'datetime'].includes(String(placed.input.inputType)));
  };
  /**
   * Checks the refs of an OData source's effective dates: each must be known on the screen and
   * hold a date (a date globals field, or a date or datetime input). Returns the error, or ''.
   * @param effectiveDates the source's effective dates, read as data
   * @param screenId the screen the source is read on
   * @param dependencies the input keys the source depends on, for option sources
   */
  const checkEffectiveDates = (
    effectiveDates: unknown,
    screenId: string,
    dependencies?: Set<string>
  ): string => {
    const refs = isRecord(effectiveDates) ? effectiveDaysOf(effectiveDates).filter(isRecord) : [];
    for (const ref of refs) {
      const error = checkRef(ref, `screen:${screenId}`, true);
      if (error) {
        return error;
      }
      if (
        ref.scope === 'globals' &&
        (!isGlobalField(ref.field) || GLOBALS_FIELD_TYPES[ref.field] !== 'date')
      ) {
        return 'Effective dates can only use date globals.';
      }
      if (ref.scope === 'inputs') {
        const placed = inputByRefKey.get(inputKey(ref.formId, ref.field));
        if (!placed || !['date', 'datetime'].includes(String(placed.input.inputType))) {
          return 'Effective dates can only use date or date and time inputs.';
        }
        dependencies?.add(inputKey(ref.formId, ref.field));
      }
    }
    return '';
  };
  const checkRef = (
    ref: Record<string, unknown>,
    context: string,
    allowScalarInputRefs = false
  ): string => {
    if (ref.scope === 'record') {
      if (!dataSourceIds.has(String(ref.dataSourceId)))
        return `Unknown record datasource reference "${String(ref.dataSourceId)}".`;
      const targetScreen = context.startsWith('screen:')
        ? context.slice('screen:'.length)
        : context.startsWith('action:')
        ? actionScreens.get(context.slice('action:'.length))
        : undefined;
      if (targetScreen === screens[0]?.id)
        return 'The starting screen cannot require a record field.';
    }
    if (ref.scope === 'variables' && !variableIds.has(String(ref.field)))
      return `Unknown variable reference "${String(ref.field)}".`;
    if (
      ref.scope === 'variables' &&
      (context.startsWith('screen:') || context.startsWith('inputDefault:')) &&
      variablesById.get(String(ref.field))?.type !== 'text'
    )
      return 'Variable reference has an incompatible value type.';
    if (ref.scope === 'inputs') {
      const key = inputKey(ref.formId, ref.field);
      const placed = inputByRefKey.get(key);
      if (!placed) return `Unknown input reference "${key}".`;
      const screenId = context.startsWith('screen:')
        ? context.slice('screen:'.length)
        : context.startsWith('inputDefault:')
        ? context.slice('inputDefault:'.length)
        : context.startsWith('action:')
        ? actionScreens.get(context.slice('action:'.length).split(':items:')[0])
        : undefined;
      if (screenId && !canReach(placed.screenId, screenId))
        return 'Input is not available from this screen.';
      const scalarType = inputScalarType(placed.input);
      if (
        context.startsWith('screen:') &&
        scalarType !== 'string' &&
        !(allowScalarInputRefs && (scalarType === 'number' || scalarType === 'boolean'))
      )
        return 'Input reference has an incompatible value type.';
    }
    if (ref.scope === 'item') {
      const kind = stateKinds.get(String(ref.componentId));
      const fields =
        kind === 'checklist'
          ? new Set(['label', 'owner', 'owner.name', 'owner.email', 'dueDate', 'checked'])
          : kind === 'timeline'
          ? new Set(['text', 'by', 'at'])
          : new Set<string>();
      if (!kind || !fields.has(String(ref.field)) || !context.startsWith('action:'))
        return 'Unknown or out-of-scope item reference.';
      const [actionId, itemsComponentId] = context.slice('action:'.length).split(':items:');
      const allowed = actionItemSources.get(actionId) ?? new Set<string>();
      if (!allowed.has(String(ref.componentId)) && itemsComponentId !== ref.componentId)
        return 'Item reference is not in scope for this action.';
    }
    return '';
  };
  const visitValue = (value: unknown, context: string): string => {
    if (!isRecord(value)) return '';
    if (value.type === 'ref')
      return isReferenceShape(value) ? checkRef(value, context) : 'Invalid reference shape.';
    if (value.type === 'date') return visitValue(value.from, context);
    if (value.type === 'template')
      return isRecord(value.values)
        ? Object.values(value.values)
            .map((entry) => visitValue(entry, context))
            .find(Boolean) ?? ''
        : '';
    if (value.type === 'list') return visitValue(value.item, context);
    return '';
  };
  const checkExpectedValue = (
    value: unknown,
    expected: 'text' | 'email' | 'date' | 'name' | 'message' | 'slot'
  ): string => {
    if (typeof value === 'string') {
      if (expected === 'email' && !z.email().safeParse(value).success)
        return 'Expected an email value.';
      if (expected === 'date' && !z.iso.date().safeParse(value).success)
        return 'Expected a date value.';
      if (expected === 'name' && (!value.trim() || value.trim().length > 120))
        return 'Expected a valid owner name.';
      return '';
    }
    if (!isRecord(value)) return 'Expected a supported value.';
    if (value.type === 'ref') {
      if (!strictValueRefSchema.safeParse(value).success)
        return 'Expected a supported value reference.';
      if (value.scope === 'record') return '';
      if (value.scope === 'item')
        return expected === 'name' || (expected === 'email' && value.field !== 'owner.email')
          ? 'Item reference has an incompatible value type.'
          : '';
      if (expected === 'slot' || expected === 'message' || expected === 'text') return '';
      if (value.scope === 'globals') {
        const type = isGlobalField(value.field) ? GLOBALS_FIELD_TYPES[value.field] : 'text';
        return expected === 'name' || type === expected
          ? ''
          : 'Reference has an incompatible value type.';
      }
      if (value.scope === 'variables') {
        const type = variablesById.get(String(value.field))?.type;
        return type === expected || (expected === 'name' && type === 'text')
          ? ''
          : 'Reference has an incompatible value type.';
      }
      if (value.scope === 'inputs') {
        const input = inputByRefKey.get(inputKey(value.formId, value.field))?.input;
        return input && inputScalarType(input) === 'string'
          ? ''
          : 'Reference has an incompatible value type.';
      }
    }
    if (value.type === 'date')
      return expected === 'date' || expected === 'slot'
        ? checkExpectedValue(value.from, 'date')
        : 'Moved dates are only valid where a date is expected.';
    if (value.type === 'template' && ['name', 'message', 'slot'].includes(expected)) {
      if (typeof value.template !== 'string' || !isRecord(value.values))
        return 'Template shape is invalid.';
      const templateValues = value.values;
      const slotPattern = /\{([a-z][a-zA-Z0-9_]{0,31})\}/g;
      const slots = [...value.template.matchAll(slotPattern)].map(([, slot]) => slot);
      const unmatchedText = value.template.replace(slotPattern, '');
      const unmatched = unmatchedText.includes('{') || unmatchedText.includes('}');
      if (
        unmatched ||
        slots.some((slot) => !Object.hasOwn(templateValues, slot)) ||
        Object.keys(templateValues).some((key) => !slots.includes(key))
      )
        return 'Template slots and values do not match.';
      return (
        Object.values(templateValues)
          .map((entry) => checkExpectedValue(entry, expected === 'name' ? 'name' : 'slot'))
          .find(Boolean) ?? ''
      );
    }
    if (value.type === 'list' && expected === 'slot') return checkExpectedValue(value.item, 'slot');
    return 'Value has an incompatible shape.';
  };
  const checkTarget = (target: unknown, allowAction: boolean): string => {
    if (!isTargetShape(target, allowAction) || !isRecord(target))
      return 'Invalid or unsupported navigation target.';
    if (target.type === 'screen' && !screenIds.has(String(target.screenId)))
      return `Unknown target screen "${String(target.screenId)}".`;
    if (target.type === 'action' && !actionIds.has(String(target.actionId)))
      return `Unknown target action "${String(target.actionId)}".`;
    return '';
  };
  for (const screen of screens) {
    for (const value of [...(screen.header?.title ?? []), ...(screen.header?.subtitle ?? [])]) {
      const error = visitValue(value, `screen:${screen.id}`);
      if (error) return error;
    }
    const hasLocalState = (items: unknown[], id: string): boolean =>
      items.some((item) => {
        if (!isRecord(item)) return false;
        if (item.kind === 'grid' && Array.isArray(item.items))
          return item.items.some(
            (entry) => isRecord(entry) && hasLocalState([entry.component], id)
          );
        if (item.kind === 'card') return hasLocalState([item.content], id);
        return item.id === id && (item.kind === 'checklist' || item.kind === 'timeline');
      });
    const walk = (component: Record<string, unknown>): string => {
      if (component.kind === 'grid' && Array.isArray(component.items))
        return (
          component.items
            .map((item) =>
              isRecord(item) && isRecord(item.component)
                ? walk(item.component)
                : 'Invalid grid item.'
            )
            .find(Boolean) ?? ''
        );
      if (component.kind === 'card')
        return isRecord(component.content) ? walk(component.content) : 'Card content is invalid.';
      if (component.kind === 'button' || component.kind === 'list') {
        if (component.target !== undefined) {
          const error = checkTarget(component.target, component.kind === 'button');
          if (error) return error;
        }
      }
      if (component.kind === 'form') {
        const submit = component.submit;
        if (isRecord(submit) && submit.target !== undefined) {
          const error = checkTarget(submit.target, true);
          if (error) return error;
        }
      }
      /* Buttons may run actions on any screen; the backend checks what a list screen action may do. */
      if (component.kind === 'button' && isRecord(component.showWhen)) {
        const targetId = String(component.showWhen.componentId);
        const target = components.find((candidate) => candidate.id === targetId);
        if (
          !stateComponents.has(targetId) ||
          !target ||
          (target.kind !== 'checklist' && target.kind !== 'timeline') ||
          !hasLocalState(screen.components, targetId)
        )
          return 'showWhen must reference a state component.';
      }
      if (
        (component.kind === 'checklist' || component.kind === 'timeline') &&
        screen.kind !== 'detail'
      )
        return 'Checklists and timelines are only valid on detail screens.';
      if (component.kind === 'checklist' && Array.isArray(component.itemActions)) {
        for (const itemAction of component.itemActions) {
          if (!isRecord(itemAction)) return 'Invalid checklist item action.';
          const error = checkTarget(itemAction.target, true);
          if (error) return error;
          if (isRecord(itemAction.target) && itemAction.target.type !== 'action')
            return 'Checklist item actions must target actions.';
        }
      }
      const values: unknown[] = [];
      if (component.kind === 'text') values.push(component.text);
      if (component.kind === 'button') values.push(component.label);
      if (component.kind === 'fields' && Array.isArray(component.items))
        component.items.forEach((item) => {
          if (isRecord(item)) values.push(item.value);
        });
      for (const value of values) {
        const error = visitValue(value, `screen:${screen.id}`);
        if (error) return error;
      }
      if (
        (component.kind === 'checklist' || component.kind === 'timeline') &&
        Array.isArray(component.recordKey)
      ) {
        for (const ref of component.recordKey) {
          if (isRecord(ref)) {
            const error = checkRef(ref, `screen:${screen.id}`);
            if (error) return error;
          }
        }
      }
      const inputList =
        component.kind === 'form' && Array.isArray(component.inputs)
          ? component.inputs
          : component.kind === 'input'
          ? [component]
          : [];
      for (const input of inputList) {
        if (!isRecord(input)) continue;
        const currentInputKey = inputKey(
          component.kind === 'form' ? component.id : undefined,
          input.id
        );
        const dependencies = inputDependencies.get(currentInputKey) ?? new Set<string>();
        if (input.initialValue !== undefined) {
          const error = visitValue(input.initialValue, `inputDefault:${screen.id}`);
          if (error) return error;
          if (isRecord(input.initialValue) && input.initialValue.type === 'ref') {
            const ref = input.initialValue;
            if (ref.scope === 'inputs') {
              const refKey = inputKey(ref.formId, ref.field);
              dependencies.add(refKey);
              const target = inputByRefKey.get(refKey);
              if (!target || target.screenId === screen.id || !canReach(target.screenId, screen.id))
                return 'Input defaults must reference an input available from another screen.';
              if (
                inputScalarType(target.input) !== inputScalarType(input) ||
                (inputScalarType(input) === 'array' && target.input.valueType !== input.valueType)
              )
                return 'Initial input reference must match the input value type.';
            } else if (ref.scope !== 'record') {
              const expected = inputScalarType(input);
              const variableType =
                ref.scope === 'variables' ? variablesById.get(String(ref.field))?.type : undefined;
              if (expected !== 'string' || (ref.scope === 'variables' && variableType !== 'text'))
                return 'Input reference does not match the input value type.';
            }
          }
        }
        if (isRecord(input.options) && isRecord(input.options.odata)) {
          for (const filter of Array.isArray(input.options.odata.rowFilters)
            ? input.options.odata.rowFilters
            : []) {
            if (isRecord(filter) && isRecord(filter.value)) {
              const error = checkRef(filter.value, `screen:${screen.id}`, true);
              if (error) return error;
              if (filter.value.scope === 'inputs')
                dependencies.add(inputKey(filter.value.formId, filter.value.field));
              if (
                !hasRequiredODataFieldType(filter.value, filter.fieldType) ||
                needsInputFieldType(filter.value, filter.fieldType)
              )
                return 'Date/time references in OData filters require fieldType.';
            }
            if (isRecord(filter) && isRecord(filter.when) && isRecord(filter.when.value)) {
              const error = checkRef(filter.when.value, `screen:${screen.id}`, true);
              if (error) return error;
              dependencies.add(inputKey(filter.when.value.formId, filter.when.value.field));
            }
          }
          const effectiveDatesError = checkEffectiveDates(
            input.options.odata.effectiveDates,
            screen.id,
            dependencies
          );
          if (effectiveDatesError) return effectiveDatesError;
        }
        inputDependencies.set(currentInputKey, dependencies);
      }
      if (component.kind === 'list') {
        const sources = Array.isArray(component.dataSource)
          ? component.dataSource.filter(isRecord)
          : [];
        const sourcesById = new Map(sources.map((source) => [String(source.id), source]));
        for (const source of sources) {
          for (const filter of Array.isArray(source.rowFilters) ? source.rowFilters : []) {
            if (isRecord(filter) && isRecord(filter.value)) {
              const error = checkRef(filter.value, `screen:${screen.id}`, true);
              if (error) return error;
              if (
                !hasRequiredODataFieldType(filter.value, filter.fieldType) ||
                needsInputFieldType(filter.value, filter.fieldType)
              )
                return 'Date/time references in OData filters require fieldType.';
            }
            if (isRecord(filter) && isRecord(filter.when) && isRecord(filter.when.value)) {
              const error = checkRef(filter.when.value, `screen:${screen.id}`, true);
              if (error) return error;
            }
          }
          const effectiveDatesError = checkEffectiveDates(source.effectiveDates, String(screen.id));
          if (effectiveDatesError) return effectiveDatesError;
        }
        for (const column of Array.isArray(component.columns) ? component.columns : []) {
          const refs = !isRecord(column)
            ? []
            : column.kind === 'computed' && isRecord(column.inputs)
            ? Object.values(column.inputs).flatMap((entry) =>
                Array.isArray(entry) ? entry : [entry]
              )
            : column.kind === 'person'
            ? [column.primary, column.secondary].filter(isRecord)
            : column.kind === 'status' && isRecord(column.value)
            ? [column.value]
            : isRecord(column.value)
            ? [column.value]
            : [];
          for (const ref of refs) {
            if (!isRecord(ref) || ref.scope !== 'dataSource') continue;
            const source = sourcesById.get(String(ref.dataSourceId));
            if (!source) return 'List column references an unknown datasource.';
            if (!fieldAvailable(source, String(ref.field)))
              return 'List column references an unavailable field.';
          }
        }
        for (const join of Array.isArray(component.joins) ? component.joins : []) {
          if (!isRecord(join)) continue;
          const source = sourcesById.get(String(join.dataSourceId));
          if (!source) return 'List join references an unknown datasource.';
          if (isRecord(join.on) && join.on.scope === 'dataSource') {
            const onSource = sourcesById.get(String(join.on.dataSourceId));
            if (!onSource) return 'List join references an unknown datasource.';
            if (!fieldAvailable(onSource, String(join.on.field)))
              return 'List join references an unavailable field.';
          }
          if (!fieldAvailable(source, String(join.field)))
            return 'List join references an unavailable field.';
        }
      }
      if (
        component.kind === 'metric' &&
        isRecord(component.dataSource) &&
        Array.isArray(component.dataSource.rowFilters)
      ) {
        for (const filter of component.dataSource.rowFilters) {
          if (!isRecord(filter)) continue;
          if (isRecord(filter.value)) {
            const error = checkRef(filter.value, `screen:${screen.id}`, true);
            if (error) return error;
            if (
              !hasRequiredODataFieldType(filter.value, filter.fieldType) ||
              needsInputFieldType(filter.value, filter.fieldType)
            )
              return 'Date/time references in OData filters require fieldType.';
          }
          if (isRecord(filter.when) && isRecord(filter.when.value)) {
            const error = checkRef(filter.when.value, `screen:${screen.id}`, true);
            if (error) return error;
          }
        }
      }
      return '';
    };
    for (const component of screen.components) {
      if (isRecord(component)) {
        const error = walk(component);
        if (error) return error;
      }
    }
  }
  const dependencyState = new Map<string, 'visiting' | 'done'>();
  const visitInput = (key: string): boolean => {
    dependencyState.set(key, 'visiting');
    for (const target of inputDependencies.get(key) ?? []) {
      if (
        dependencyState.get(target) === 'visiting' ||
        (dependencyState.get(target) === undefined &&
          inputDependencies.has(target) &&
          !visitInput(target))
      )
        return false;
    }
    dependencyState.set(key, 'done');
    return true;
  };
  for (const key of inputDependencies.keys())
    if (!dependencyState.has(key) && !visitInput(key))
      return 'Input defaults and option filters cannot contain dependency cycles.';
  for (const action of definition.actions ?? []) {
    if (!actionScreens.has(action.id)) return `Action "${action.id}" is not used by a button.`;
    if (!actionScreens.get(action.id)) return `Action "${action.id}" must run from one screen.`;
    for (const step of action.steps) {
      if (step.kind === 'store') {
        const targetComponent = components.find(
          (component) => component.id === step.target.componentId
        );
        if (
          !targetComponent ||
          (targetComponent.kind !== 'checklist' && targetComponent.kind !== 'timeline')
        )
          return `Unknown state component target "${step.target.componentId}".`;
        if (componentScreens.get(step.target.componentId) !== actionScreens.get(action.id))
          return 'Action and state target must be on the same screen.';
        for (const rowValue of step.rows as unknown[]) {
          if (
            !isRecord(rowValue) ||
            (targetComponent.kind === 'timeline' &&
              (!hasOnlyKeys(rowValue, ['text']) ||
                typeof rowValue.text !== 'string' ||
                !rowValue.text.trim() ||
                rowValue.text.length > 2000)) ||
            (targetComponent.kind === 'checklist' &&
              (!hasOnlyKeys(rowValue, ['label', 'owner', 'dueDate']) ||
                typeof rowValue.label !== 'string' ||
                !rowValue.label.trim() ||
                rowValue.label.length > 200 ||
                (rowValue.dueDate !== undefined && !isDateGivingValue(rowValue.dueDate))))
          )
            return 'Write row does not match its target component.';
          if (targetComponent.kind === 'checklist' && rowValue.owner !== undefined) {
            const ownerError = ownerShapeError(rowValue.owner);
            if (
              ownerError ||
              !isRecord(rowValue.owner) ||
              !hasOnlyKeys(rowValue.owner, ['name', 'email']) ||
              rowValue.owner.email === undefined
            )
              return ownerError || 'Write owner does not match the strict v3 contract.';
            const owner = rowValue.owner;
            if (
              !(
                (typeof owner.email === 'string' && z.email().safeParse(owner.email).success) ||
                strictValueRefSchema.safeParse(owner.email).success
              ) ||
              (owner.name !== undefined && !isNameValue(owner.name))
            )
              return 'Write owner values do not match the strict v3 contract.';
          }
        }
        for (const row of step.rows) {
          if (!isRecord(row)) continue;
          const rowRecord = row as Record<string, unknown>;
          for (const value of Object.values(rowRecord)) {
            const values = isRecord(value) && 'email' in value ? Object.values(value) : [value];
            for (const entry of values) {
              const error = visitValue(entry, `action:${action.id}`);
              if (error) return error;
            }
          }
          if (isRecord(rowRecord.owner)) {
            const emailError = checkExpectedValue(rowRecord.owner.email, 'email');
            if (emailError) return emailError;
            if (rowRecord.owner.name !== undefined) {
              const nameError = checkExpectedValue(rowRecord.owner.name, 'name');
              if (nameError) return nameError;
            }
          }
          if (rowRecord.dueDate !== undefined) {
            const dueDateError = checkExpectedValue(rowRecord.dueDate, 'date');
            if (dueDateError) return dueDateError;
          }
        }
      } else if (step.kind === 'send') {
        const valueContext = `action:${action.id}${
          step.items ? `:items:${step.items.componentId}` : ''
        }`;
        const address = isRecord(step.to) && step.to.type === 'email' ? step.to.address : step.to;
        for (const value of [address, step.subject, step.text]) {
          const error = visitValue(value, valueContext);
          if (error) return error;
        }
        const recipientError = checkExpectedValue(address, 'email');
        if (recipientError) return recipientError;
        for (const value of [step.subject, step.text]) {
          if (value === undefined) continue;
          const messageError = checkExpectedValue(value, 'message');
          if (messageError) return messageError;
          const containsList = (candidate: unknown): boolean =>
            isRecord(candidate) &&
            (candidate.type === 'list' ||
              (candidate.type === 'template' &&
                isRecord(candidate.values) &&
                Object.values(candidate.values).some(containsList)));
          if (containsList(value) && !step.items)
            return 'Template lists need a checklist on the send step.';
        }
        if (step.groupBy !== undefined && !step.items)
          return 'Grouping by recipient needs checklist items.';
        if (
          step.items &&
          !components.some(
            (component) =>
              component.id === step.items?.componentId && component.kind === 'checklist'
          )
        )
          return `Unknown checklist target "${step.items.componentId}".`;
        if (
          step.items &&
          componentScreens.get(step.items.componentId) !== actionScreens.get(action.id)
        )
          return 'Action items must reference a checklist on the action screen.';
      }
    }
  }
  return '';
}

/**
 * Lists the components of one screen, including the ones inside Grid and Card.
 * @param components the screen's top-level components
 */
const screenLeaves = (components: unknown[]): Record<string, unknown>[] =>
  components.flatMap((component): Record<string, unknown>[] => {
    if (!isRecord(component)) {
      return [];
    }
    if (component.kind === 'grid' && Array.isArray(component.items)) {
      return screenLeaves(component.items.map((item) => (isRecord(item) ? item.component : null)));
    }
    if (component.kind === 'card') {
      return screenLeaves([component.content]);
    }
    return [component];
  });

/* Columns that can make bar and donut categories, or split a chart into series. */
const CATEGORY_COLUMN_LOOKS = ['text', 'number', 'status'];

/**
 * Tells whether a list column reads the list's primary data source and is one of the allowed
 * looks: a field column type (`text`, `number`, `date`) or `status` for a status column.
 * @param column the list column
 * @param primaryDataSourceId the list's primary data source id
 * @param allowed the field column types, and `status`, that the chart may use here
 */
const isChartColumn = (
  column: Record<string, unknown>,
  primaryDataSourceId: unknown,
  allowed: string[]
): boolean => {
  const { kind, value, type } = column;
  if (!isRecord(value) || value.dataSourceId !== primaryDataSourceId) {
    return false;
  }
  return kind === 'status'
    ? allowed.includes('status')
    : (kind === undefined || kind === 'field') && allowed.includes(String(type));
};

/**
 * Checks each screen's charts against the lists they read: at most four per screen, `from`
 * names a list on the same screen, and every column the chart names is one it can use.
 */
function validateCharts(definition: FabrikAppDefinition): string {
  for (const screen of definition.screens) {
    if (!isRecord(screen) || !Array.isArray(screen.components)) {
      continue;
    }
    const leaves = screenLeaves(screen.components);
    const charts = leaves.filter((component) => component.kind === 'chart');
    if (charts.length > MAX_CHARTS_PER_SCREEN) {
      return `Screen "${String(screen.id)}" has more than ${MAX_CHARTS_PER_SCREEN} charts.`;
    }
    for (const chart of charts) {
      const { id, type, from, groupBy, splitBy, measure } = chart;
      const list = leaves.find((component) => component.kind === 'list' && component.id === from);
      if (!list) {
        return `Chart "${String(id)}" must read a list on the same screen.`;
      }
      const { columns, primaryDataSourceId } = list;
      /**
       * Finds the list column with this name.
       * @param name the column name the chart gives
       */
      const columnNamed = (name: unknown) =>
        (Array.isArray(columns) ? columns : []).find(
          (column): column is Record<string, unknown> => isRecord(column) && column.name === name
        );
      const groupColumn = columnNamed(groupBy);
      const groupLooks = type === 'line' ? ['date'] : CATEGORY_COLUMN_LOOKS;
      if (!groupColumn || !isChartColumn(groupColumn, primaryDataSourceId, groupLooks)) {
        return `Chart "${String(id)}" groupBy must name a ${
          type === 'line' ? 'date column' : 'text, number or status column'
        } of list "${String(from)}" that reads its primary data source.`;
      }
      const splitColumn = splitBy === undefined ? undefined : columnNamed(splitBy);
      if (
        splitBy !== undefined &&
        (!splitColumn || !isChartColumn(splitColumn, primaryDataSourceId, CATEGORY_COLUMN_LOOKS))
      ) {
        return `Chart "${String(
          id
        )}" splitBy must name a text, number or status column of list "${String(
          from
        )}" that reads its primary data source.`;
      }
      const measureColumn =
        isRecord(measure) && measure.fn !== 'count' ? columnNamed(measure.column) : undefined;
      if (
        isRecord(measure) &&
        measure.fn !== 'count' &&
        (!measureColumn || !isChartColumn(measureColumn, primaryDataSourceId, ['number']))
      ) {
        return `Chart "${String(id)}" measure must name a number column of list "${String(
          from
        )}" that reads its primary data source.`;
      }
    }
  }
  return '';
}

/**
 * Parses and checks a definition. Returns it with an error message, or empty text when valid.
 * @param value definition as JSON text
 * @param options emailsHidden: true for a hosted app, whose user groups come without emails
 */
export function parseDefinition(value: string, { emailsHidden = false } = {}) {
  try {
    const parsed = JSON.parse(value) as Partial<FabrikAppDefinition>;
    if (
      parsed.schemaVersion !== 3 ||
      !parsed.app ||
      typeof parsed.app.name !== 'string' ||
      !Array.isArray(parsed.screens)
    ) {
      return {
        definition: undefined,
        error: 'Definition must include schemaVersion, app, and screens.',
      };
    }
    const definition = parsed as FabrikAppDefinition;
    const error =
      validateStrictShapes(definition) ||
      validateDefinitionLinks(definition) ||
      validateLayoutShapes(definition) ||
      validateListConfiguration(definition) ||
      validateVariables(definition) ||
      validateUserGroups(definition, emailsHidden) ||
      validateItemActions(definition) ||
      validateActions(definition) ||
      validateCharts(definition);
    if (!error) {
      for (const component of eachComponent(definition)) {
        if (component.kind === 'list' && component.limit === undefined) {
          component.limit = DEFAULT_LIST_PAGE_SIZE;
        }
      }
    }
    return { definition, error };
  } catch (error) {
    return {
      definition: undefined,
      error: error instanceof Error ? error.message : 'Invalid JSON',
    };
  }
}
